import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { JSX } from 'react/jsx-runtime';
import { Chat, Message } from '../types';
import { Send, Paperclip, Image, X, ArrowLeft, Reply, Search, Smile, CalendarPlus, ArrowDown, ArrowUp, FileText, Check, Copy, Pencil, Trash2 } from 'lucide-react';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { chatService, resolveEligibleStudents, selectScheduleCandidates, EligibleStudent, MentionCandidate, MY_TELEGRAM_ID } from '../api/chats';
import { apiClient, handleUnauthorized } from '../api/client';
import './ChatWindow.css';

const ProtectedImage: React.FC<{ src: string; alt: string; className?: string; onClick?: () => void; onLoad?: () => void }> = ({ src, alt, className, onClick, onLoad }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    let localUrl: string | null = null;

    const fetchImage = async () => {
      // If src is already a blob or data URL (optimistic UI), use it directly
      if (src.startsWith('blob:') || src.startsWith('data:')) {
        if (active) {
          setObjectUrl(src);
          setLoading(false);
          onLoad?.();
        }
        return;
      }

      try {
        const response = await apiClient.get(src, { responseType: 'blob' });
        if (active) {
          localUrl = URL.createObjectURL(response.data);
          setObjectUrl(localUrl);
          setLoading(false);
          onLoad?.();
        }
      } catch (err) {
        console.error("Failed to load image", err);
        if (active) {
          setError(true);
          setLoading(false);
        }
      }
    };

    fetchImage();

    return () => {
      active = false;
      if (localUrl) {
        URL.revokeObjectURL(localUrl);
      }
    };
  }, [src]);

  if (loading) return <div className="image-placeholder">Загрузка...</div>;
  if (error || !objectUrl) return <div className="image-error">Ошибка загрузки</div>;

  return <img
    src={objectUrl}
    alt={alt}
    className={className}
    onClick={onClick}
    style={onClick ? { cursor: 'pointer' } : undefined}
  />;
};

const ProtectedAudio: React.FC<{ src: string; className?: string }> = ({ src, className }) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    let localUrl: string | null = null;

    const fetchAudio = async () => {
      // If src is already a blob or data URL, use it directly
      if (src.startsWith('blob:') || src.startsWith('data:')) {
        if (active) {
          setObjectUrl(src);
          setLoading(false);
        }
        return;
      }

      try {
        const response = await apiClient.get(src, { responseType: 'blob' });
        if (active) {
          localUrl = URL.createObjectURL(response.data);
          setObjectUrl(localUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error("Failed to load audio", err);
        if (active) {
          setError(true);
          setLoading(false);
        }
      }
    };

    fetchAudio();

    return () => {
      active = false;
      if (localUrl) {
        URL.revokeObjectURL(localUrl);
      }
    };
  }, [src]);

  if (loading) return <div className="audio-loading">Загрузка аудио...</div>;
  if (error || !objectUrl) return <div className="audio-error">Ошибка загрузки аудио</div>;

  return (
    <audio controls className={className} controlsList="nodownload">
      <source src={objectUrl} type="audio/ogg" />
      <source src={objectUrl} type="audio/mpeg" />
      Your browser does not support the audio element.
    </audio>
  );
};

interface TouchState {
  startX: number;
  startY: number;
  startTime: number;
  isTracking: boolean;
  currentX: number;
  hasMoved: boolean;
}

interface Report {
  id: number;
  report_text: string;
  week_start_date: string;
  created_at: string;
  teacher_name: string;
  teacher_id?: number;
}

interface ProgramItem {
  id: number;
  section: string;
  topic: string;
  is_completed: boolean;
  order_index: number;
}

interface ChatWindowProps {
  chat: Chat | null;
  messages: Message[];
  onSendMessage: (text: string, attachments?: File[], replyToMessageId?: number) => void;
  onEditMessage: (chatId: string, messageId: number, text: string) => Promise<void>;
  onDeleteMessage: (chatId: string, messageId: number) => Promise<void>;
  onToggleReaction?: (chatId: string, message: Message, emoji: string) => void;
  allowedReactions?: string[];
  onDraftChange?: (chatId: string, draft: { text: string; html: string } | null) => void;
  draft?: { text: string; html: string } | null;
  onUpdateUnreadCount?: (chatId: string, unreadCount: number) => void;
  onLoadOlderMessages?: (chatId: string, offset: number, limit: number) => Promise<number>;
  onBack?: () => void;
}

const getPlural = (n: number, one: string, two: string, five: string) => {
  const absN = Math.abs(n) % 100;
  const n1 = absN % 10;
  if (absN > 10 && absN < 20) return five;
  if (n1 > 1 && n1 < 5) return two;
  if (n1 === 1) return one;
  return five;
};

const ChatWindow: React.FC<ChatWindowProps> = ({ chat, messages, onSendMessage, onEditMessage, onDeleteMessage, onToggleReaction, allowedReactions, onDraftChange, draft, onUpdateUnreadCount, onLoadOlderMessages, onBack }) => {
  const [newMessage, setNewMessage] = useState('');
  const [newMessageHtml, setNewMessageHtml] = useState('');
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  const [editingMessage, setEditingMessage] = useState<Message | null>(null);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
  const [pendingDeleteMessage, setPendingDeleteMessage] = useState<Message | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [touchState, setTouchState] = useState<TouchState>({
    startX: 0,
    startY: 0,
    startTime: 0,
    isTracking: false,
    currentX: 0,
    hasMoved: false
  });
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMatchIndex, setSearchMatchIndex] = useState(-1);
  const searchTermValue = searchTerm.trim();
  const isSearchActive = searchTermValue.length >= 3;
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState<string | null>(null);
  const [loadingImagesCount, setLoadingImagesCount] = useState(0);
  const [currentDateInView, setCurrentDateInView] = useState<Date | null>(null);
  const [eligibleStudents, setEligibleStudents] = useState<EligibleStudent[]>([]);
  const [mentionCandidates, setMentionCandidates] = useState<MentionCandidate[]>([]);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [activeMentionIndex, setActiveMentionIndex] = useState(-1);
  const [mentionAnchorIndex, setMentionAnchorIndex] = useState<number | null>(null);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [showStudentPicker, setShowStudentPicker] = useState(false);
  const [showReportsStudentPicker, setShowReportsStudentPicker] = useState(false);
  const [scheduleUrl, setScheduleUrl] = useState<string | null>(null);
  const [selectedStudentName, setSelectedStudentName] = useState<string | null>(null);
  const [modalPos, setModalPos] = useState({ x: 0, y: 0 });
  const [isDraggingModal, setIsDraggingModal] = useState(false);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [reportsTab, setReportsTab] = useState<'reports' | 'program'>('reports');
  const [reports, setReports] = useState<Report[]>([]);
  const [program, setProgram] = useState<ProgramItem[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsStudentName, setReportsStudentName] = useState<string | null>(null);
  const [reportsStudentId, setReportsStudentId] = useState<number | null>(null);
  const [reportsModalPos, setReportsModalPos] = useState({ x: 0, y: 0 });
  const [isDraggingReportsModal, setIsDraggingReportsModal] = useState(false);
  const [isSafariSchedulePositioned, setIsSafariSchedulePositioned] = useState(false);
  const [isSafariReportsPositioned, setIsSafariReportsPositioned] = useState(false);
  const [copiedAvatarMessageId, setCopiedAvatarMessageId] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const membersCacheRef = useRef<Map<string, any[]>>(new Map());
  const draftsByChatRef = useRef<Map<string, { text: string; html: string }>>(new Map());
  const activeDraftChatIdRef = useRef<string | null>(null);
  const newMessageRef = useRef('');
  const newMessageHtmlRef = useRef('');
  const reportsModalDragStartPos = useRef({ x: 0, y: 0 });
  const scheduleModalRef = useRef<HTMLDivElement>(null);
  const reportsModalRef = useRef<HTMLDivElement>(null);
  const copiedMemberTimeoutRef = useRef<number | null>(null);
  const [initialScrolledChatId, setInitialScrolledChatId] = useState<string | null>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [localUnreadCount, setLocalUnreadCount] = useState<number | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isVirtualKeyboardVisible, setIsVirtualKeyboardVisible] = useState<boolean | null>(null);
  const [isScheduleCompact, setIsScheduleCompact] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLDivElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pendingPrependRef = useRef<{
    chatId: string;
    prevHeight: number;
    prevScrollTop: number;
  } | null>(null);
  const isLoadingOlderRef = useRef(false);
  const hasMoreOlderRef = useRef(true);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const reactionPickerRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const isAtVeryBottomRef = useRef(true);
  const [isAtVeryBottom, setIsAtVeryBottom] = useState(true);
  const overlayMouseDownRef = useRef(false);
  const reactionLongPressRef = useRef<number | null>(null);
  const isSafari = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent.toLowerCase();
    return ua.includes('safari') && !ua.includes('chrome') && !ua.includes('crios') && !ua.includes('fxios') && !ua.includes('edgios') && !ua.includes('android');
  }, []);
  const [formatMenu, setFormatMenu] = useState({ x: 0, y: 0, visible: false });
  const formatSelectionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const isEditing = Boolean(editingMessage);
  const isComposerTyping = newMessage.trim().length > 0;
  const isScheduleButtonCompact = isMobile ? isComposerTyping : isScheduleCompact;
  const pendingReplyScrollIdRef = useRef<number | null>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const highlightTimeoutRef = useRef<number | null>(null);
  const lastSearchTargetRef = useRef<{ term: string; messageKey: string; matchIndex: number } | null>(null);
  const virtualKeyboardBaselineRef = useRef<number | null>(null);
  const isIPad = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    const userAgent = navigator.userAgent.toLowerCase();
    const platform = (navigator.platform || '').toLowerCase();
    return userAgent.includes('ipad') || (platform === 'macintel' && navigator.maxTouchPoints > 1);
  }, []);
  const previousSearchTermRef = useRef<string>('');

  const formatLessonsInfo = (lessonsTotal: number, lessonsScheduled: number) => {
    const remaining = lessonsTotal - lessonsScheduled;
    const remainingText = `${remaining} ${getPlural(remaining, 'занятие', 'занятия', 'занятий')}`;
    if (lessonsScheduled === 0) {
      return remainingText;
    }
    const scheduledText = `${lessonsScheduled} ${getPlural(lessonsScheduled, 'запланировано', 'запланированы', 'запланировано')}`;
    return `${remainingText} + ${scheduledText}`;
  };

  const suppressNearBottomCheckUntilRef = useRef<number | null>(null);

  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    const container = messagesContainerRef.current;
    if (!container) return;
    // Suppress near-bottom check during smooth scroll animation
    if (behavior === 'smooth') {
      suppressNearBottomCheckUntilRef.current = Date.now() + 500;
      isNearBottomRef.current = true;
      setIsNearBottom(true);
      isAtVeryBottomRef.current = true;
      setIsAtVeryBottom(true);
    }
    container.scrollTo({ top: container.scrollHeight, behavior });
  };

  // Larger threshold for "near bottom" (for auto-scroll on new messages)
  const NEAR_BOTTOM_THRESHOLD = 150;
  // Strict threshold for "at very bottom" (for hiding divider and marking as read)
  const AT_BOTTOM_THRESHOLD = 10;

  const updateIsNearBottom = () => {
    // Skip check if suppressed (during smooth scroll animation)
    if (suppressNearBottomCheckUntilRef.current && Date.now() < suppressNearBottomCheckUntilRef.current) {
      return;
    }
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;

    // Check "near bottom" for auto-scroll on new messages
    const nearBottom = distanceToBottom <= NEAR_BOTTOM_THRESHOLD;
    isNearBottomRef.current = nearBottom;
    setIsNearBottom(nearBottom);

    // Check "at very bottom" for marking as read and hiding divider
    const atVeryBottom = distanceToBottom <= AT_BOTTOM_THRESHOLD;
    isAtVeryBottomRef.current = atVeryBottom;
    setIsAtVeryBottom(atVeryBottom);
  };

  const triggerLoadOlder = useCallback(() => {
    if (!chat || isLoadingOlderRef.current || !hasMoreOlderRef.current || !onLoadOlderMessages) return;
    const container = messagesContainerRef.current;
    if (!container) return;

    const prevHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;
    pendingPrependRef.current = { chatId: chat.id, prevHeight, prevScrollTop };
    setIsLoadingOlder(true);

    const limit = 50;
    void onLoadOlderMessages(chat.id, messages.length, limit).then((addedCount) => {
      if (addedCount === 0 || addedCount < limit) {
        setHasMoreOlder(false);
      }
      if (addedCount === 0) {
        pendingPrependRef.current = null;
      }
    }).finally(() => {
      setIsLoadingOlder(false);
    });
  }, [chat, messages.length, onLoadOlderMessages]);

  const handleMessagesScroll = () => {
    updateIsNearBottom();

    if (!chat) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    if (container.scrollTop > 40) return;

    triggerLoadOlder();
  };

  const handleMessagesBlankClick = (_event: React.MouseEvent<HTMLDivElement>) => {
    if (!isSearching) return;
    setIsSearching(false);
    setSearchTerm('');
  };

  const findMessageIndexByReplyId = useCallback((replyId?: number) => {
    if (!replyId) return -1;
    return messages.findIndex((msg) => {
      if (msg.telegramMessageId === replyId) return true;
      const numericId = Number(msg.id);
      return Number.isFinite(numericId) && numericId === replyId;
    });
  }, [messages]);

  const attemptScrollToReply = useCallback(() => {
    const replyId = pendingReplyScrollIdRef.current;
    if (!replyId) return;
    const index = findMessageIndexByReplyId(replyId);
    if (index >= 0) {
      pendingReplyScrollIdRef.current = null;
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        const element = container?.querySelector(`[data-message-index="${index}"]`);
        if (element) {
          element.scrollIntoView({ block: 'center' });
        }
        const targetMessage = messages[index];
        if (targetMessage) {
          const highlightId = targetMessage.telegramMessageId
            ? `tg:${targetMessage.telegramMessageId}`
            : `id:${targetMessage.id}`;
          setHighlightedMessageId(highlightId);
          if (highlightTimeoutRef.current) {
            window.clearTimeout(highlightTimeoutRef.current);
          }
          highlightTimeoutRef.current = window.setTimeout(() => {
            setHighlightedMessageId(null);
            highlightTimeoutRef.current = null;
          }, 1600);
        }
      });
      return;
    }
    if (hasMoreOlderRef.current && !isLoadingOlderRef.current) {
      triggerLoadOlder();
    } else {
      pendingReplyScrollIdRef.current = null;
    }
  }, [findMessageIndexByReplyId, triggerLoadOlder]);

  useEffect(() => {
    attemptScrollToReply();
  }, [attemptScrollToReply, messages]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);


  const handleImageLoad = () => {
    setLoadingImagesCount(prev => Math.max(0, prev - 1));
    if (isNearBottomRef.current) {
      requestAnimationFrame(() => scrollToBottom('auto'));
    }
  };

  // Подсчитываем количество изображений в сообщениях
  const countImagesInMessages = (msgs: Message[]) => {
    return msgs.reduce((count, message) => {
      if (message.attachments) {
        return count + message.attachments.filter(att => att.type === 'image').length;
      }
      return count;
    }, 0);
  };

  // Use useRef to track the ID we last ATTEMPTED to mark as read.
  // Refs update synchronously and persist without causing re-renders, perfect for loop breaking.
  const lastMarkedIdRef = useRef<number | null>(null);
  const lastMarkedIndexRef = useRef<number | null>(null);
  const suppressMarkReadUntilRef = useRef<number | null>(null);
  const activeChatIdRef = useRef<string | null>(null);
  // Store the initial first unread index when chat opens - don't change it while reading
  const [initialFirstUnreadIndex, setInitialFirstUnreadIndex] = useState<number | null>(null);
  const [showUnreadDivider, setShowUnreadDivider] = useState(false);

  const unreadIncomingMeta = useMemo(() => {
    const incoming = messages.filter(message => message.sender !== 'me');
    const flaggedUnread = incoming.filter(message => message.isRead === false);
    const hasIncomingReadFlags = incoming.some(message => message.isRead === true || message.isRead === false);
    return {
      incoming,
      flaggedUnread,
      hasIncomingReadFlags,
      ownUnreadCount: messages.filter(message => message.sender === 'me' && message.isRead === false).length
    };
  }, [messages]);

  const unreadIncomingCount = useMemo(() => {
    if (unreadIncomingMeta.hasIncomingReadFlags) return unreadIncomingMeta.flaggedUnread.length;
    const totalUnread = chat?.unreadCount ?? 0;
    return Math.max(totalUnread - unreadIncomingMeta.ownUnreadCount, 0);
  }, [chat?.unreadCount, unreadIncomingMeta]);

  // Calculate first unread index only for initial positioning
  const calculatedFirstUnreadIndex = useMemo(() => {
    if (!chat || messages.length === 0) return null;
    if (unreadIncomingMeta.hasIncomingReadFlags) {
      const firstUnread = messages.findIndex(message => message.isRead === false && message.sender !== 'me');
      return firstUnread >= 0 ? firstUnread : null;
    }
    const unreadCount = unreadIncomingCount;
    if (unreadCount <= 0) return null;
    const incomingMessages = unreadIncomingMeta.incoming;
    if (incomingMessages.length === 0) return null;
    const unreadStartIndex = Math.max(0, incomingMessages.length - unreadCount);
    const firstUnreadMessage = incomingMessages[unreadStartIndex];
    const index = messages.findIndex(message => message.id === firstUnreadMessage.id);
    return index >= 0 ? index : null;
  }, [chat?.id, unreadIncomingCount, messages, unreadIncomingMeta]);

  const calculatedFirstUnreadAnyIndex = useMemo(() => {
    if (!chat || messages.length === 0) return null;
    const firstUnread = messages.findIndex(message => message.isRead === false);
    return firstUnread >= 0 ? firstUnread : null;
  }, [chat?.id, messages]);

  // Use stored index for display, calculated for scrolling
  const firstUnreadIndex = showUnreadDivider ? initialFirstUnreadIndex : null;

  const countIncomingAfter = (startIndex: number, currentMessages: Message[] = messages) => {
    const hasReadFlags = currentMessages.some((message) => message.isRead === true || message.isRead === false);
    return currentMessages.reduce((count, message, index) => {
      if (index <= startIndex) return count;
      if (hasReadFlags) {
        return message.isRead === false ? count + 1 : count;
      }
      return count + 1;
    }, 0);
  };

  useEffect(() => {
    if (!isSearchActive && isNearBottomRef.current) {
      scrollToBottom('auto');
    }
  }, [messages, isSearchActive, chat, localUnreadCount]);

  // Устанавливаем счетчик изображений при изменении сообщений
  useEffect(() => {
    const imageCount = countImagesInMessages(messages);
    setLoadingImagesCount(imageCount);
  }, [messages]);

  // Отслеживаем загрузку изображений и скроллим вниз когда все загрузятся
  useEffect(() => {
    if (loadingImagesCount === 0 && messages.length > 0 && isNearBottomRef.current) {
      const rafId = requestAnimationFrame(() => scrollToBottom('auto'));
      return () => cancelAnimationFrame(rafId);
    }
  }, [loadingImagesCount, messages.length]);

  // Use refs to access latest values in IntersectionObserver callback
  const chatRef = useRef(chat);
  const localUnreadCountRef = useRef(localUnreadCount);
  const messagesRef = useRef(messages);
  const markReadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingMarkRef = useRef<{ messageId: number; messageIndex: number; chatId: string } | null>(null);
  const autoMarkedOwnUnreadRef = useRef<string | number | null>(null);

  useEffect(() => {
    chatRef.current = chat;
  }, [chat]);

  useEffect(() => {
    localUnreadCountRef.current = localUnreadCount;
  }, [localUnreadCount]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Track last processed message ID to detect new messages
  const lastProcessedMessageIdRef = useRef<string | number | null>(null);

  // Mark all messages as read (for quick scroll to bottom)
  const markAllAsRead = async () => {
    if (!chat) return;

    // Find the last incoming message
    const lastIncomingMessage = [...messages].reverse().find(msg => msg.sender !== 'me');
    if (!lastIncomingMessage) return;

    // Get the message ID to mark
    let messageIdToMark: number | undefined = lastIncomingMessage.telegramMessageId;
    if (messageIdToMark === undefined) {
      const numericId = Number(lastIncomingMessage.id);
      if (!isNaN(numericId)) {
        messageIdToMark = numericId;
      }
    }

    if (messageIdToMark === undefined) return;

    const lastIndex = messages.findIndex(msg => msg.id === lastIncomingMessage.id);

    // Always reset UI state
    setLocalUnreadCount(0);
    setShowUnreadDivider(false);
    setInitialFirstUnreadIndex(null);

    // Skip API call if already marked this message
    if (lastMarkedIdRef.current === messageIdToMark) {
      if (onUpdateUnreadCount && chat) onUpdateUnreadCount(chat.id, 0);
      return;
    }

    try {
      lastMarkedIdRef.current = messageIdToMark;
      lastMarkedIndexRef.current = lastIndex;
      await chatService.markRead(chat.id, messageIdToMark);
      if (onUpdateUnreadCount) onUpdateUnreadCount(chat.id, 0);
    } catch (e) {
      console.error("Failed to mark all as read", e);
    }
  };

  const resolveMessageIdToMark = (message: Message) => {
    let messageIdToMark: number | undefined = message.telegramMessageId;
    if (messageIdToMark === undefined) {
      const numericId = Number(message.id);
      if (!isNaN(numericId)) {
        messageIdToMark = numericId;
      }
    }
    return messageIdToMark;
  };

  // Auto-mark new incoming messages as read when user is in the chat
  useEffect(() => {
    // Skip if not initialized or no chat or no messages
    if (!chat || isInitializing || !initialScrolledChatId || initialScrolledChatId !== chat.id || messages.length === 0) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const lastMessageId = lastMessage.id;

    // Skip if we already processed this message
    if (lastProcessedMessageIdRef.current === lastMessageId) {
      return;
    }

    // Update ref for next comparison
    const prevLastMessageId = lastProcessedMessageIdRef.current;
    lastProcessedMessageIdRef.current = lastMessageId;

    // If this is the first time (initial load), just save the ID and return
    if (prevLastMessageId === null) {
      return;
    }

    // If the last message is from someone else (incoming)
    if (lastMessage.sender !== 'me') {
      if (isNearBottomRef.current) {
        // User is near bottom - scroll down and mark as read
        scrollToBottom('smooth');
        void markAllAsRead();
      } else {
        // User is not near bottom - show unread divider
        const lastMessageIndex = messages.length - 1;
        if (initialFirstUnreadIndex === null) {
          setInitialFirstUnreadIndex(lastMessageIndex);
        }
        setShowUnreadDivider(true);
      }
    }
  }, [messages, chat?.id, isInitializing, initialScrolledChatId, initialFirstUnreadIndex]);

  // Debounced mark as read - defined here so IntersectionObserver can use it
  const handleVisibleMessage = (messageId: number, messageIndex: number, currentChat: Chat | null) => {
    if (!currentChat) return;

    // Skip if already marked this exact message
    if (lastMarkedIdRef.current === messageId) return;

    // Only skip if we've marked a message with higher index in the SAME session
    if (lastMarkedIndexRef.current !== null && messageIndex < lastMarkedIndexRef.current) return;

    // Store the pending mark request
    pendingMarkRef.current = { messageId, messageIndex, chatId: currentChat.id };

    if (markReadTimeoutRef.current) {
      clearTimeout(markReadTimeoutRef.current);
    }

    markReadTimeoutRef.current = setTimeout(async () => {
      const pending = pendingMarkRef.current;
      if (!pending) return;

      // Check if chat changed while waiting
      if (activeChatIdRef.current !== pending.chatId) return;

      // Double check if we still need to mark it
      if (lastMarkedIdRef.current === pending.messageId) return;

      try {
        lastMarkedIdRef.current = pending.messageId;
        lastMarkedIndexRef.current = pending.messageIndex;
        await chatService.markRead(pending.chatId, pending.messageId);

        // Calculate remaining unread using the latest messages
        const currentMessages = messagesRef.current;
        const remainingUnread = countIncomingAfter(pending.messageIndex, currentMessages);

        setLocalUnreadCount(remainingUnread);
        if (onUpdateUnreadCount && currentChat) onUpdateUnreadCount(currentChat.id, remainingUnread);
      } catch (e) {
        console.error("Failed to mark read", e);
      }
    }, 200); // Fast response for smooth UX
  };

  // Отслеживаем текущую дату при скроле
  useEffect(() => {
    if (!messagesContainerRef.current) return;

    const visibleIndices = new Set<number>();

    const observer = new IntersectionObserver(
      (entries) => {
        const currentMessages = messagesRef.current;
        const currentChat = chatRef.current;
        const currentUnreadCount = localUnreadCountRef.current;

        entries.forEach((entry) => {
          const indexStr = entry.target.getAttribute('data-message-index');
          if (indexStr) {
            const index = parseInt(indexStr);
            if (entry.isIntersecting) {
              visibleIndices.add(index);
            } else {
              visibleIndices.delete(index);
            }
          }
        });

        // Находим минимальный индекс (самое верхнее сообщение)
        if (visibleIndices.size > 0) {
          const minIndex = Math.min(...Array.from(visibleIndices));
          if (minIndex >= 0 && minIndex < currentMessages.length) {
            setCurrentDateInView(new Date(currentMessages[minIndex].timestamp));
          }
        } else {
          setCurrentDateInView(null);
        }

        // Logic for marking as read based on visibility
        const fallbackUnread = currentMessages.filter(message => message.isRead === false && message.sender !== 'me').length;
        const unreadCount = currentUnreadCount ?? fallbackUnread;
        if (visibleIndices.size > 0 && currentChat && unreadCount > 0) {
          const suppressUntil = suppressMarkReadUntilRef.current;
          if (suppressUntil && Date.now() < suppressUntil) {
            return;
          }

          const maxVisibleIndex = Math.max(...Array.from(visibleIndices));
          let maxVisibleIncomingIndex: number | null = null;
          visibleIndices.forEach((index) => {
            if (index < 0 || index >= currentMessages.length) return;
            if (currentMessages[index].sender === 'me') return;
            if (maxVisibleIncomingIndex === null || index > maxVisibleIncomingIndex) {
              maxVisibleIncomingIndex = index;
            }
          });

          const targetIndex = maxVisibleIncomingIndex ?? maxVisibleIndex;
          if (targetIndex >= 0 && targetIndex < currentMessages.length) {
            const visibleMsg = currentMessages[targetIndex];

            // Determine ID
            let messageIdToMark: number | undefined = visibleMsg.telegramMessageId;
            if (messageIdToMark === undefined) {
              const numericId = Number(visibleMsg.id);
              if (!isNaN(numericId)) {
                messageIdToMark = numericId;
              }
            }

            // We debounce markRead for the lowest visible incoming message (or any if none).
            if (messageIdToMark !== undefined) {
              handleVisibleMessage(messageIdToMark, targetIndex, currentChat);
            }
          }
        }
      },
      {
        root: messagesContainerRef.current,
        threshold: [0, 0.1, 0.5],
        rootMargin: '0px'
      }
    );

    const messageElements = messagesContainerRef.current.querySelectorAll('.message');
    messageElements.forEach((element) => {
      observer.observe(element);
    });

    return () => observer.disconnect();
  }, [messages, searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(event.target as Node)) {
        setReactionPickerMessageId(null);
      }
    };

    if (reactionPickerMessageId) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [reactionPickerMessageId]);

  useEffect(() => {
    const mobileQuery = typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 768px)')
      : null;
    if (!mobileQuery) return;
    const handleMobileChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);

    setIsMobile(mobileQuery.matches);

    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', handleMobileChange);
    } else {
      mobileQuery.addListener(handleMobileChange);
    }

    return () => {
      if (mobileQuery.removeEventListener) {
        mobileQuery.removeEventListener('change', handleMobileChange);
      } else {
        mobileQuery.removeListener(handleMobileChange);
      }
    };
  }, []);

  useEffect(() => {
    setIsTouchDevice(
      typeof window !== 'undefined' &&
      (('ontouchstart' in window) || navigator.maxTouchPoints > 0)
    );
  }, []);

  useEffect(() => {
    if (!isIPad || !isTouchDevice || typeof window === 'undefined' || !window.visualViewport) {
      setIsVirtualKeyboardVisible(null);
      return;
    }

    const viewport = window.visualViewport;
    const KEYBOARD_HEIGHT_THRESHOLD = 120;

    const updateKeyboardVisibility = () => {
      const baselineHeight = virtualKeyboardBaselineRef.current ?? viewport.height;
      const nextBaseline = Math.max(baselineHeight, viewport.height);
      virtualKeyboardBaselineRef.current = nextBaseline;
      const keyboardHeight = nextBaseline - viewport.height;
      setIsVirtualKeyboardVisible(keyboardHeight > KEYBOARD_HEIGHT_THRESHOLD || viewport.offsetTop > 0);
    };

    const resetBaseline = () => {
      virtualKeyboardBaselineRef.current = viewport.height;
      updateKeyboardVisibility();
    };

    resetBaseline();
    viewport.addEventListener('resize', updateKeyboardVisibility);
    window.addEventListener('orientationchange', resetBaseline);

    return () => {
      viewport.removeEventListener('resize', updateKeyboardVisibility);
      window.removeEventListener('orientationchange', resetBaseline);
    };
  }, [isIPad, isTouchDevice]);

  useEffect(() => {
    setIsLoadingOlder(false);
    setHasMoreOlder(true);
    pendingPrependRef.current = null;
    setIsSearching(false);
    setSearchTerm('');
    setReactionPickerMessageId(null);
  }, [chat?.id]);

  useEffect(() => {
    isLoadingOlderRef.current = isLoadingOlder;
  }, [isLoadingOlder]);

  useEffect(() => {
    hasMoreOlderRef.current = hasMoreOlder;
  }, [hasMoreOlder]);

  const updateScheduleCompact = useCallback(() => {
    const target = chatWindowRef.current;
    if (!target) return;
    const width = target.getBoundingClientRect().width;
    setIsScheduleCompact(isMobile || width < 420);
  }, [isMobile]);

  useEffect(() => {
    updateScheduleCompact();
    const target = chatWindowRef.current;
    if (!target || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      updateScheduleCompact();
    });

    observer.observe(target);
    return () => observer.disconnect();
  }, [updateScheduleCompact]);

  useEffect(() => {
    const pending = pendingPrependRef.current;
    if (!pending || !chat || pending.chatId !== chat.id) return;
    const container = messagesContainerRef.current;
    if (!container) return;
    const nextHeight = container.scrollHeight;
    const delta = nextHeight - pending.prevHeight;
    container.scrollTop = pending.prevScrollTop + delta;
    pendingPrependRef.current = null;
  }, [messages, chat?.id]);

  const getMemberIdFromChat = (member: any): number | null => {
    const rawId = member?.id ?? member?.telegram_id ?? member?.user_id ?? member?.sender_id;
    if (typeof rawId === 'number') return rawId;
    if (typeof rawId === 'string') {
      const parsed = Number(rawId);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const getMemberDisplayName = (member: any): string | null => {
    const name = (member?.innoprog_name || member?.full_name || '').trim();
    return name.length > 0 ? name : null;
  };

  const resolvedMembers = useMemo(() => {
    if (!chat) return [];
    const members = Array.isArray(chat.members) ? chat.members : [];
    if (members.length > 0) {
      membersCacheRef.current.set(chat.id, members);
      return members;
    }
    return membersCacheRef.current.get(chat.id) || members;
  }, [chat?.id, chat?.members]);

  const memberIndex = useMemo(() => {
    const members = resolvedMembers;
    const byId = new Map<number, any>();
    const byFullName = new Map<string, any>();
    members.forEach((member: any) => {
      const id = getMemberIdFromChat(member);
      if (id) byId.set(id, member);
      if (member?.full_name) {
        byFullName.set(member.full_name.trim().toLowerCase(), member);
      }
    });
    return { members, byId, byFullName };
  }, [resolvedMembers]);

  const memberCandidates = useMemo(() => {
    const seen = new Set<number>();
    const candidates: { id: number; fallbackName?: string; preferredName?: string; isMe?: boolean; role?: string }[] = [];
    memberIndex.members.forEach((member: any) => {
      const id = getMemberIdFromChat(member);
      if (!id || seen.has(id)) return;
      seen.add(id);
      candidates.push({
        id,
        fallbackName: member.full_name || member.innoprog_name || undefined,
        preferredName: getMemberDisplayName(member) || undefined,
        isMe: id === MY_TELEGRAM_ID,
        role: member.role
      });
    });
    return candidates;
  }, [memberIndex]);

  useEffect(() => {
    if (!chat) {
      setEligibleStudents([]);
      setMentionCandidates([]);
      return;
    }

    const mentionableMap = new Map<number, MentionCandidate>();
    memberCandidates.forEach((candidate) => {
      if (candidate.isMe) return;
      const name = candidate.preferredName || candidate.fallbackName || `ID ${candidate.id}`;
      mentionableMap.set(candidate.id, { id: candidate.id, name });
    });
    setMentionCandidates(Array.from(mentionableMap.values()));

    const scheduleCandidates = selectScheduleCandidates(memberCandidates);
    if (scheduleCandidates.length === 0) {
      setEligibleStudents([]);
      return;
    }

    let isActive = true;
    const loadEligibleStudents = async () => {
      const resolved = await resolveEligibleStudents(scheduleCandidates);
      if (isActive) {
        setEligibleStudents(Array.isArray(resolved) ? resolved : []);
      }
    };

    loadEligibleStudents();

    return () => {
      isActive = false;
    };
  }, [chat?.id, memberCandidates]);

  useEffect(() => {
    if (!showMentionSuggestions) return;
    setActiveMentionIndex(-1);
  }, [mentionQuery, showMentionSuggestions]);

  useEffect(() => {
    return () => {
      if (markReadTimeoutRef.current) clearTimeout(markReadTimeoutRef.current);
    };
  }, []);

  /* 
   * Removed aggressive auto-read effect. 
   * Now handled by IntersectionObserver via handleVisibleMessage.
   */

  useEffect(() => {
    if (!chat) {
      setInitialScrolledChatId(null);
      setLocalUnreadCount(null);
      activeChatIdRef.current = null;
      setIsInitializing(false);
      lastProcessedMessageIdRef.current = null;
      autoMarkedOwnUnreadRef.current = null;
      return;
    }
    setReplyingTo(null);
    setNewMessage('');
    setAttachments([]);
    setShowMentionSuggestions(false);
    setMentionQuery('');
    lastMarkedIdRef.current = null;
    lastMarkedIndexRef.current = null;
    suppressMarkReadUntilRef.current = null;
    lastProcessedMessageIdRef.current = null;
    autoMarkedOwnUnreadRef.current = null;
    // Don't set isNearBottom/isAtVeryBottom to true until we actually scroll - prevents premature divider hiding
    isNearBottomRef.current = false;
    setIsNearBottom(false);
    isAtVeryBottomRef.current = false;
    setIsAtVeryBottom(false);
    setLocalUnreadCount(unreadIncomingCount);
    activeChatIdRef.current = chat.id;
    // Reset unread divider state for new chat
    setInitialFirstUnreadIndex(null);
    setShowUnreadDivider(false);
    // Show loading state until we scroll to the right position
    setIsInitializing(true);
  }, [chat?.id]);

  useEffect(() => {
    if (!chat) return;
    if (activeChatIdRef.current !== chat.id) return;
    setLocalUnreadCount(prev => {
      if (prev === null) return unreadIncomingCount;
      return Math.min(prev, unreadIncomingCount);
    });
  }, [chat?.id, unreadIncomingCount]);

  // Mark all as read and hide unread divider when scrolled to the VERY bottom
  useEffect(() => {
    if (isAtVeryBottom && showUnreadDivider && !isInitializing) {
      // Mark all messages as read when user reaches very bottom
      void markAllAsRead();
      // Small delay for smooth UX before hiding divider
      const timer = setTimeout(() => {
        setShowUnreadDivider(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isAtVeryBottom, showUnreadDivider, isInitializing]);

  useEffect(() => {
    if (!chat || messages.length === 0 || initialScrolledChatId === chat.id) return;

    const performInitialScroll = () => {
      const hasIncomingUnread = unreadIncomingCount > 0 && calculatedFirstUnreadIndex !== null;
      const targetIndex = calculatedFirstUnreadIndex ?? calculatedFirstUnreadAnyIndex;

      if (hasIncomingUnread && calculatedFirstUnreadIndex !== null) {
        // Store initial unread index and show divider
        setInitialFirstUnreadIndex(calculatedFirstUnreadIndex);
        setShowUnreadDivider(true);

        // Wait for React to re-render the divider, then scroll to it
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const container = messagesContainerRef.current;
            if (container) {
              // First try to scroll to the unread divider
              const dividerElement = container.querySelector('[data-unread-divider="true"]');
              if (dividerElement) {
                dividerElement.scrollIntoView({ block: 'center' });
              } else {
                // Fallback: scroll to the first unread message
                const element = container.querySelector(`[data-message-index="${calculatedFirstUnreadIndex}"]`);
                if (element) {
                  element.scrollIntoView({ block: 'center' });
                }
              }
              // Update isNearBottom/isAtVeryBottom based on where we scrolled
              const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
              const nearBottom = distanceToBottom <= NEAR_BOTTOM_THRESHOLD;
              const atVeryBottom = distanceToBottom <= AT_BOTTOM_THRESHOLD;
              isNearBottomRef.current = nearBottom;
              setIsNearBottom(nearBottom);
              isAtVeryBottomRef.current = atVeryBottom;
              setIsAtVeryBottom(atVeryBottom);
            }
            setInitialScrolledChatId(chat.id);
            setIsInitializing(false);
          });
        });
        return;
      }

      if (targetIndex !== null) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const container = messagesContainerRef.current;
            const element = container?.querySelector(`[data-message-index="${targetIndex}"]`);
            if (element) {
              element.scrollIntoView({ block: 'center' });
            }
            if (container) {
              const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
              const nearBottom = distanceToBottom <= NEAR_BOTTOM_THRESHOLD;
              const atVeryBottom = distanceToBottom <= AT_BOTTOM_THRESHOLD;
              isNearBottomRef.current = nearBottom;
              setIsNearBottom(nearBottom);
              isAtVeryBottomRef.current = atVeryBottom;
              setIsAtVeryBottom(atVeryBottom);
            }
            setInitialScrolledChatId(chat.id);
            setIsInitializing(false);
          });
        });
        return;
      }

      // Default: scroll to bottom
      scrollToBottom('auto');
      isNearBottomRef.current = true;
      setIsNearBottom(true);
      isAtVeryBottomRef.current = true;
      setIsAtVeryBottom(true);
      setInitialScrolledChatId(chat.id);
      setIsInitializing(false);
    };

    // Delay to ensure DOM is fully rendered before scrolling
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(performInitialScroll);
      });
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [chat, messages, initialScrolledChatId, calculatedFirstUnreadIndex, calculatedFirstUnreadAnyIndex, unreadIncomingCount]);

  useEffect(() => {
    if (!chat || isInitializing || initialScrolledChatId !== chat.id) return;
    if (calculatedFirstUnreadAnyIndex === null) return;
    const ownUnreadMessage = messages[calculatedFirstUnreadAnyIndex];
    if (!ownUnreadMessage || ownUnreadMessage.sender !== 'me') return;
    if (calculatedFirstUnreadIndex !== null && calculatedFirstUnreadAnyIndex > calculatedFirstUnreadIndex) return;
    const markId = resolveMessageIdToMark(ownUnreadMessage);
    if (markId === undefined || autoMarkedOwnUnreadRef.current === markId) return;
    autoMarkedOwnUnreadRef.current = markId;
    void chatService.markRead(chat.id, markId);
  }, [
    chat?.id,
    isInitializing,
    initialScrolledChatId,
    calculatedFirstUnreadAnyIndex,
    calculatedFirstUnreadIndex,
    messages
  ]);

  useEffect(() => {
    const isAnyMiniAppOpen = Boolean(scheduleUrl || showReportsModal || showStudentPicker || fullScreenImage);
    if (isAnyMiniAppOpen) {
      document.body.classList.add('mini-app-open');
    } else {
      document.body.classList.remove('mini-app-open');
    }
  }, [scheduleUrl, showReportsModal, showStudentPicker, fullScreenImage]);

  useEffect(() => {
    if (!isSafari || !scheduleUrl) return;
    setIsSafariSchedulePositioned(false);
    const id = window.requestAnimationFrame(() => {
      const element = scheduleModalRef.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      setModalPos({ x: rect.left, y: rect.top });
      setIsSafariSchedulePositioned(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, [isSafari, scheduleUrl]);

  useEffect(() => {
    if (!isSafari || !showReportsModal) return;
    setIsSafariReportsPositioned(false);
    const id = window.requestAnimationFrame(() => {
      const element = reportsModalRef.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      setReportsModalPos({ x: rect.left, y: rect.top });
      setIsSafariReportsPositioned(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, [isSafari, showReportsModal]);

  const handleModalMouseDown = (e: React.MouseEvent) => {
    if (isMobile) return;
    e.preventDefault();
    setIsDraggingModal(true);
    dragStartPos.current = {
      x: e.clientX - modalPos.x,
      y: e.clientY - modalPos.y
    };
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingModal) return;
      setModalPos({
        x: e.clientX - dragStartPos.current.x,
        y: e.clientY - dragStartPos.current.y
      });
    };

    const handleMouseUp = () => {
      setIsDraggingModal(false);
    };

    if (isDraggingModal) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingModal]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDraggingReportsModal) return;
      setReportsModalPos({
        x: e.clientX - reportsModalDragStartPos.current.x,
        y: e.clientY - reportsModalDragStartPos.current.y
      });
    };

    const handleMouseUp = () => {
      setIsDraggingReportsModal(false);
    };

    if (isDraggingReportsModal) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingReportsModal]);

  useEffect(() => {
    if (!showReportsModal) {
      setReportsModalPos({ x: 0, y: 0 });
      setReportsStudentName(null);
      setReportsStudentId(null);
      setReports([]);
      setProgram([]);
    }
  }, [showReportsModal]);

  useEffect(() => {
    return () => {
      if (copiedMemberTimeoutRef.current !== null) {
        window.clearTimeout(copiedMemberTimeoutRef.current);
      }
    };
  }, []);

  const handleCopyMemberId = async (messageId: string, senderId?: number) => {
    if (!senderId) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(senderId));
      } else {
        await writeClipboardPayload(String(senderId), `<span>${senderId}</span>`);
      }
      setCopiedAvatarMessageId(messageId);
      if (copiedMemberTimeoutRef.current !== null) {
        window.clearTimeout(copiedMemberTimeoutRef.current);
      }
      copiedMemberTimeoutRef.current = window.setTimeout(() => {
        setCopiedAvatarMessageId((prev) => (prev === messageId ? null : prev));
      }, 1200);
    } catch (error) {
      console.error('Failed to copy member id:', error);
    }
  };

  const normalizeSenderName = (name?: string) => {
    if (!name) return '';
    return name.replace(/^@/, '').trim().toLowerCase();
  };

  const resolveMemberForSender = (senderId?: number, senderRawName?: string, senderFallback?: string) => {
    if (senderId && memberIndex.byId.has(senderId)) {
      return memberIndex.byId.get(senderId);
    }
    const normalized = normalizeSenderName(senderRawName || senderFallback);
    if (normalized && memberIndex.byFullName.has(normalized)) {
      return memberIndex.byFullName.get(normalized);
    }
    return null;
  };

  const resolveDisplayNameForSender = (senderId?: number, senderRawName?: string, senderFallback?: string) => {
    const member = resolveMemberForSender(senderId, senderRawName, senderFallback);
    const displayName = member ? getMemberDisplayName(member) : null;
    return displayName || senderFallback || 'Unknown';
  };

  const mentionNameById = useMemo(() => {
    const map = new Map<number, string>();
    mentionCandidates.forEach((candidate) => {
      map.set(candidate.id, candidate.name);
    });
    return map;
  }, [mentionCandidates]);

  const escapeRegExp = (value: string) => {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const normalizeSpecialInnoprogMentions = (value: string) => {
    if (!value) return value;
    let normalized = value.replace(/(^|[^\w@])@?администратор\s+innoprog(?=$|\s|[.,!?:;)\]])/giu, '$1@innoprog_admin');
    normalized = normalized.replace(/(^|[^\w@])@?координатор\s+innoprog(?=$|\s|[.,!?:;)\]])/giu, '$1@innoprog_coordinator');
    return normalized;
  };

  const buildMentionPattern = (name: string) => {
    const escapedName = escapeRegExp(name);
    // Allow mention to continue with regular sentence text.
    // But do NOT match if the next token starts with uppercase (avoids partial matches like "@Артемий Королев" for "@Артемий").
    return new RegExp(`(^|\\s)@${escapedName}(?=$|[.,!?:;)]|\\s+(?!\\p{Lu}))`, 'gu');
  };

  const formatMentions = (text: string) => {
    return text.replace(/\{\{(\d+)\}\}/g, (match, id) => {
      const name = mentionNameById.get(Number(id));
      return name ? `@${name}` : match;
    });
  };

  const replaceMemberMentions = (text: string) => {
    if (!text) return text;
    let result = text;
    const members = [...memberIndex.members].sort((a: any, b: any) => {
      const aName = String(a?.full_name || '');
      const bName = String(b?.full_name || '');
      return bName.length - aName.length;
    });
    members.forEach((member: any) => {
      if (!member?.innoprog_name || !member?.full_name) return;
      const pattern = buildMentionPattern(member.full_name);
      result = result.replace(pattern, `$1@${member.innoprog_name}`);
    });
    return normalizeSpecialInnoprogMentions(result);
  };

  const decodeIncomingText = (text: string, isIncoming: boolean) => {
    if (!text || !isIncoming) return text;
    return text.replace(/&lt;/g, '<');
  };

  const escapeHtml = (value: string) => {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  };

  const isBlockElement = (node: Node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    const tag = (node as HTMLElement).tagName.toLowerCase();
    return tag === 'div' || tag === 'p' || tag === 'li' || tag === 'pre' || tag === 'ul' || tag === 'ol';
  };

  const serializeChildNodes = (element: HTMLElement) => {
    let output = '';
    let prevWasInline = false;
    Array.from(element.childNodes).forEach((child) => {
      const childIsBlock = isBlockElement(child);
      if (childIsBlock && prevWasInline && !output.endsWith('\n')) {
        output += '\n';
      }
      output += serializeTelegramHtml(child);
      if (child.nodeType === Node.TEXT_NODE) {
        prevWasInline = (child.nodeValue || '').length > 0;
      } else {
        prevWasInline = !childIsBlock;
      }
    });
    return output;
  };

  const serializeTelegramHtml = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtml(node.nodeValue || '').replace(/\u00A0/g, ' ');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    const children = serializeChildNodes(element);

    if (tag === 'br') return '\n';
    if (tag === 'b' || tag === 'strong') return `<b>${children}</b>`;
    if (tag === 'i' || tag === 'em') return `<i>${children}</i>`;
    if (tag === 'u') return `<u>${children}</u>`;
    if (tag === 's' || tag === 'strike' || tag === 'del') return `<s>${children}</s>`;
    if (tag === 'code') return `<code>${children}</code>`;
    if (tag === 'pre') return `<pre>${children}</pre>`;
    if (tag === 'div' || tag === 'p') {
      if (children === '' || children === '\n') return '\n';
      return `${children}\n`;
    }
    if (tag === 'li') {
      if (children === '' || children === '\n') return '\n';
      return `${children}\n`;
    }
    if (tag === 'ul' || tag === 'ol' || tag === 'span' || tag === 'font') return children;
    return children;
  };

  const replaceMentionsForSend = (html: string) => {
    if (!html) return '';
    const container = document.createElement('div');
    container.innerHTML = html;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    let current = walker.nextNode();
    while (current) {
      const node = current as Text;
      let text = normalizeSpecialInnoprogMentions(node.nodeValue || '');
      const candidates = [...mentionCandidates].sort((a, b) => b.name.length - a.name.length);
      candidates.forEach((candidate) => {
        const pattern = buildMentionPattern(candidate.name);
        text = text.replace(pattern, `$1{{${candidate.id}}}`);
      });
      node.nodeValue = text;
      current = walker.nextNode();
    }
    return serializeTelegramHtml(container).replace(/\u00A0/g, ' ');
  };

  const trimMessageHtmlForTelegram = (html: string) => {
    if (!html) return '';
    let result = html.replace(/\r\n/g, '\n');
    result = result.replace(/<br\s*\/?>/gi, '\n');
    result = result.replace(/^[\s\n]+/g, '');
    result = result.replace(/[\s\n]+$/g, '');
    return result;
  };

  const normalizeMentionText = (value: string) => value.toLowerCase().replace(/\s+/g, ' ').trim();

  const getTextBeforeCursor = (value: string, cursorPosition?: number, editor?: HTMLElement) => {
    if (editor) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        if (editor.contains(range.endContainer)) {
          const beforeRange = range.cloneRange();
          beforeRange.selectNodeContents(editor);
          beforeRange.setEnd(range.endContainer, range.endOffset);
          return beforeRange.toString();
        }
      }
    }
    const cursorPos = cursorPosition ?? value.length;
    return value.slice(0, cursorPos);
  };

  const getTextAfterCursor = (editor: HTMLElement) => {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editor.contains(range.endContainer)) {
        const afterRange = range.cloneRange();
        afterRange.selectNodeContents(editor);
        afterRange.setStart(range.endContainer, range.endOffset);
        return afterRange.toString();
      }
    }
    return '';
  };

  const getTextNodeAtOffset = (root: HTMLElement, offset: number) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode() as Text | null;
    let remaining = offset;
    while (node) {
      const len = node.nodeValue?.length ?? 0;
      if (remaining <= len) {
        return { node, offset: remaining };
      }
      remaining -= len;
      node = walker.nextNode() as Text | null;
    }
    return null;
  };

  const updateMentionState = (value: string, cursorPosition?: number, editor?: HTMLElement) => {
    const beforeCursor = getTextBeforeCursor(value, cursorPosition, editor);
    const atIndex = beforeCursor.lastIndexOf('@');
    if (atIndex < 0) {
      setShowMentionSuggestions(false);
      setMentionQuery('');
      setMentionAnchorIndex(null);
      return;
    }
    const query = beforeCursor.slice(atIndex + 1);
    const cleanedQuery = normalizeMentionText(query);
    setMentionQuery(cleanedQuery);
    setMentionAnchorIndex(atIndex);
    setShowMentionSuggestions(true);
  };

  const insertTextAtCursor = (text: string) => {
    const element = messageInputRef.current;
    if (!element) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.setEndAfter(node);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const handleSelectMention = (candidate: MentionCandidate) => {
    const editor = messageInputRef.current;
    if (!editor) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.endContainer)) return;

    const beforeCursor = getTextBeforeCursor(editor.innerText, undefined, editor);
    const anchorIndex = mentionAnchorIndex ?? beforeCursor.lastIndexOf('@');
    if (anchorIndex < 0) return;
    const charBeforeAnchor = anchorIndex > 0 ? beforeCursor[anchorIndex - 1] : '';

    const anchorPos = getTextNodeAtOffset(editor, anchorIndex);
    if (!anchorPos) return;

    const afterCursor = getTextAfterCursor(editor);
    const mentionText = `@${candidate.name}`;
    const spacer = afterCursor.startsWith(' ') || afterCursor.startsWith('\n') ? '' : ' ';
    const leadingSpace = charBeforeAnchor === '(' ? ' ' : '';

    const replaceRange = range.cloneRange();
    replaceRange.setStart(anchorPos.node, anchorPos.offset);
    replaceRange.deleteContents();
    const mentionNode = document.createTextNode(`${leadingSpace}${mentionText}${spacer}`);
    replaceRange.insertNode(mentionNode);
    replaceRange.setStartAfter(mentionNode);
    replaceRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(replaceRange);

    setNewMessage(editor.innerText);
    setNewMessageHtml(editor.innerHTML);
    setShowMentionSuggestions(false);
    setMentionQuery('');
    setMentionAnchorIndex(null);
    editor.focus();
  };

  const handleMessageChange = (event: React.FormEvent<HTMLDivElement>) => {
    const editor = event.currentTarget;
    const text = editor.innerText;
    const html = editor.innerHTML;
    setNewMessage(text);
    setNewMessageHtml(html);
    updateMentionState(text, undefined, editor);
  };

  const applyInlineFormat = (command: 'bold' | 'italic' | 'underline' | 'strikeThrough') => {
    const editor = messageInputRef.current;
    if (!editor) return;
    editor.focus();
    document.execCommand(command);
    setShowMentionSuggestions(false);
    setMentionQuery('');
    setMentionAnchorIndex(null);
    setFormatMenu({ x: 0, y: 0, visible: false });
    setNewMessage(editor.innerText);
    setNewMessageHtml(editor.innerHTML);
    updateMentionState(editor.innerText, undefined, editor);
  };

  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    const editor = messageInputRef.current;
    if (!editor) return;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;
    event.preventDefault();
    const offset = 48;
    setFormatMenu({ x: event.clientX, y: event.clientY - offset, visible: true });
  };

  useEffect(() => {
    if (!formatMenu.visible) return;
    const handleClose = () => setFormatMenu(prev => ({ ...prev, visible: false }));
    window.addEventListener('mousedown', handleClose);
    return () => {
      window.removeEventListener('mousedown', handleClose);
    };
  }, [formatMenu.visible]);

  useEffect(() => {
    const editor = messageInputRef.current;
    if (!editor) return;
    editor.style.height = 'auto';
    const styles = window.getComputedStyle(editor);
    const lineHeight = parseFloat(styles.lineHeight || '0') || 20;
    const paddingTop = parseFloat(styles.paddingTop || '0') || 0;
    const paddingBottom = parseFloat(styles.paddingBottom || '0') || 0;
    const minHeight = 43;
    editor.style.minHeight = `${minHeight}px`;
    const maxHeight = lineHeight * 7 + paddingTop + paddingBottom;
    const nextHeight = Math.min(editor.scrollHeight, maxHeight);
    const clampedHeight = Math.max(minHeight, nextHeight);
    editor.style.height = `${clampedHeight}px`;
    editor.style.overflowY = editor.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [newMessageHtml]);

  const mentionSuggestions = useMemo(() => {
    const normalizedQuery = mentionQuery;
    return mentionCandidates
      .filter((candidate) => {
        if (!normalizedQuery) return true;
        return normalizeMentionText(candidate.name).includes(normalizedQuery);
      })
      .sort((a, b) => a.name.localeCompare(b.name, 'ru-RU'));
  }, [mentionCandidates, mentionQuery]);

  const highlightNames = useMemo(() => {
    const names = new Set<string>();
    mentionCandidates.forEach((candidate) => {
      if (candidate.name) {
        names.add(candidate.name);
      }
    });
    return Array.from(names);
  }, [mentionCandidates]);

  const highlightRegex = useMemo(() => {
    const namesPattern = highlightNames.map(escapeRegExp).join('|');
    const pattern = namesPattern ? `(@(?:${namesPattern})|@куратор)` : '(@куратор)';
    return new RegExp(`(^|\\s)${pattern}(?=\\s|$|[.,!?:;])`, 'gi');
  }, [highlightNames, escapeRegExp]);

  const searchMatchIndices = useMemo(() => {
    if (!isSearchActive) return [];
    const lowerTerm = searchTermValue.toLowerCase();
    const indices: number[] = [];
    messages.forEach((message, index) => {
      const raw = message.senderRawName?.trim();
      const sender = message.sender?.trim();
      if (raw === '@Group' || sender === '@Group') return;
      if ((message.text || '').toLowerCase().includes(lowerTerm)) {
        indices.push(index);
      }
    });
    return indices;
  }, [messages, searchTermValue, isSearchActive]);

  const searchMatchCount = searchMatchIndices.length;
  const searchPosition = searchMatchIndex >= 0 ? searchMatchIndex + 1 : 0;

  const handlePrevSearchMatch = useCallback(() => {
    if (!isSearchActive || searchMatchCount === 0) return;
    setSearchMatchIndex((prev) => (prev <= 0 ? searchMatchCount - 1 : prev - 1));
  }, [isSearchActive, searchMatchCount]);

  const handleNextSearchMatch = useCallback(() => {
    if (!isSearchActive || searchMatchCount === 0) return;
    setSearchMatchIndex((prev) => (prev >= searchMatchCount - 1 ? 0 : prev + 1));
  }, [isSearchActive, searchMatchCount]);

  useEffect(() => {
    if (!isSearchActive) {
      setSearchMatchIndex(-1);
      setHighlightedMessageId(null);
      lastSearchTargetRef.current = null;
      previousSearchTermRef.current = searchTermValue;
      return;
    }
    if (searchTermValue !== previousSearchTermRef.current) {
      previousSearchTermRef.current = searchTermValue;
      setSearchMatchIndex(searchMatchCount > 0 ? searchMatchCount - 1 : -1);
    }
  }, [isSearchActive, searchTermValue, searchMatchCount]);

  useEffect(() => {
    if (!isSearchActive) return;
    if (searchMatchCount === 0) {
      if (searchMatchIndex !== -1) {
        setSearchMatchIndex(-1);
      }
      setHighlightedMessageId(null);
      return;
    }
    if (searchMatchIndex < 0) {
      setSearchMatchIndex(searchMatchCount - 1);
      return;
    }
    if (searchMatchIndex >= searchMatchCount) {
      setSearchMatchIndex(searchMatchCount - 1);
    }
  }, [isSearchActive, searchMatchCount, searchMatchIndex]);

  useEffect(() => {
    if (!isSearchActive) return;
    if (searchMatchIndex < 0 || searchMatchIndex >= searchMatchCount) return;
    const targetIndex = searchMatchIndices[searchMatchIndex];
    const targetMessage = messages[targetIndex];
    if (!targetMessage) return;

    const highlightKey = targetMessage.telegramMessageId
      ? `tg:${targetMessage.telegramMessageId}`
      : `id:${targetMessage.id}`;

    const lastTarget = lastSearchTargetRef.current;
    if (lastTarget && lastTarget.term === searchTermValue && lastTarget.messageKey === highlightKey && lastTarget.matchIndex === searchMatchIndex) {
      return;
    }
    lastSearchTargetRef.current = { term: searchTermValue, messageKey: highlightKey, matchIndex: searchMatchIndex };

    requestAnimationFrame(() => {
      const container = messagesContainerRef.current;
      const element = container?.querySelector(`[data-message-index="${targetIndex}"]`);
      if (element) {
        element.scrollIntoView({ block: 'center' });
      }
      setHighlightedMessageId(highlightKey);
      if (highlightTimeoutRef.current) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = window.setTimeout(() => {
        setHighlightedMessageId(null);
        highlightTimeoutRef.current = null;
      }, 1600);
    });
  }, [isSearchActive, searchMatchIndex, searchMatchCount, searchMatchIndices, messages, searchTermValue]);

  const renderHighlightedText = (text: string, keyPrefix = 'hl') => {
    const formatted = replaceMemberMentions(formatMentions(text));
    if (!highlightRegex) return formatted;

    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    const matches = Array.from(formatted.matchAll(highlightRegex));
    if (matches.length === 0) return formatted;

    matches.forEach((match, index) => {
      const matchIndex = match.index ?? 0;
      const leading = match[1] ?? '';
      const highlighted = match[2] ?? '';

      if (matchIndex > lastIndex) {
        elements.push(formatted.slice(lastIndex, matchIndex));
      }
      if (leading) {
        elements.push(leading);
      }
      elements.push(
        <span key={`${keyPrefix}-${index}-${matchIndex}`} className="mention-highlight">
          {highlighted}
        </span>
      );

      lastIndex = matchIndex + match[0].length;
    });

    if (lastIndex < formatted.length) {
      elements.push(formatted.slice(lastIndex));
    }

    return elements;
  };

  const normalizeNodes = (nodes: React.ReactNode | React.ReactNode[]) => {
    return Array.isArray(nodes) ? nodes : [nodes];
  };

  const applyLineBreaksToNodes = (nodes: React.ReactNode[], keyPrefix: string): React.ReactNode[] => {
    const output: React.ReactNode[] = [];
    let brIndex = 0;

    const pushNode = (node: React.ReactNode, nestedKey: string) => {
      if (typeof node === 'string') {
        const parts = node.split('\n');
        parts.forEach((part, index) => {
          if (part) output.push(part);
          if (index < parts.length - 1) {
            output.push(<br key={`${nestedKey}-br-${brIndex}`} />);
            brIndex += 1;
          }
        });
        return;
      }
      if (React.isValidElement<any>(node)) {
        const children = (node.props as any)?.children;
        if (children === undefined) {
          output.push(node);
          return;
        }
        const childArray = Array.isArray(children) ? children : [children];
        const nextChildren = applyLineBreaksToNodes(childArray, `${nestedKey}-child`);
        output.push(React.cloneElement(node, undefined, nextChildren));
        return;
      }
      output.push(node);
    };

    nodes.forEach((node, index) => {
      pushNode(node, `${keyPrefix}-${index}`);
    });

    return output;
  };

  const linkifyText = (text: string, keyPrefix: string) => {
    const urlRegex = /((https?:\/\/|www\.)[^\s<]+)/gi;
    const matches = Array.from(text.matchAll(urlRegex));
    if (matches.length === 0) return [text];

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    matches.forEach((match, index) => {
      const start = match.index ?? 0;
      const raw = match[0];
      if (start < lastIndex) return;
      if (start > lastIndex) {
        parts.push(text.slice(lastIndex, start));
      }

      let url = raw;
      let trailing = '';
      while (url.length > 0 && /[.,!?:;)\]]$/.test(url)) {
        trailing = url.slice(-1) + trailing;
        url = url.slice(0, -1);
      }

      if (url.length === 0) {
        parts.push(raw);
        lastIndex = start + raw.length;
        return;
      }

      const href = url.startsWith('http') ? url : `https://${url}`;
      parts.push(
        <a
          key={`${keyPrefix}-link-${index}-${start}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="message-link"
        >
          {url}
        </a>
      );

      if (trailing) {
        parts.push(trailing);
      }

      lastIndex = start + raw.length;
    });

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  };

  const linkifyNodes = (nodes: React.ReactNode[], keyPrefix: string) => {
    const result: React.ReactNode[] = [];
    nodes.forEach((node, index) => {
      if (typeof node === 'string') {
        result.push(...linkifyText(node, `${keyPrefix}-${index}`));
      } else {
        result.push(node);
      }
    });
    return result;
  };

  const htmlToPlainText = (root: HTMLElement) => {
    const parts: string[] = [];
    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        parts.push((node.nodeValue || '').replace(/\u00A0/g, ' '));
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const element = node as HTMLElement;
      const tag = element.tagName.toLowerCase();
      if (tag === 'br') {
        parts.push('\n');
        return;
      }
      if (tag === 'div' || tag === 'p' || tag === 'li' || tag === 'pre') {
        Array.from(element.childNodes).forEach(walk);
        parts.push('\n');
        return;
      }
      Array.from(element.childNodes).forEach(walk);
    };
    Array.from(root.childNodes).forEach(walk);
    return parts.join('').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  };

  const stripCopyArtifacts = (root: HTMLElement) => {
    root.querySelectorAll('.mention-highlight, .search-highlight').forEach((el) => {
      const span = el as HTMLElement;
      span.replaceWith(document.createTextNode(span.textContent || ''));
    });

    root.querySelectorAll('[style]').forEach((el) => {
      const element = el as HTMLElement;
      const style = element.style;
      if (style.background || style.backgroundColor) {
        style.background = '';
        style.backgroundColor = '';
      }
      if (!style.cssText.trim()) {
        element.removeAttribute('style');
      }
    });

    root.querySelectorAll('[class]').forEach((el) => {
      (el as HTMLElement).removeAttribute('class');
    });
  };

  const convertNewlinesToBr = (root: HTMLElement) => {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const textNodes: Text[] = [];
    while (walker.nextNode()) {
      textNodes.push(walker.currentNode as Text);
    }
    textNodes.forEach((node) => {
      const value = node.nodeValue;
      if (!value || !value.includes('\n')) return;
      const parentTag = node.parentElement?.tagName.toLowerCase();
      if (parentTag === 'pre' || parentTag === 'code') return;
      const parts = value.split('\n');
      const fragment = document.createDocumentFragment();
      parts.forEach((part, index) => {
        if (part) fragment.appendChild(document.createTextNode(part));
        if (index < parts.length - 1) {
          fragment.appendChild(document.createElement('br'));
        }
      });
      node.replaceWith(fragment);
    });
  };

  const buildClipboardHtml = (container: HTMLElement) => {
    const bodyStyles = window.getComputedStyle(document.body);
    const baseFont = bodyStyles.fontFamily || 'sans-serif';
    const baseSize = bodyStyles.fontSize || '14px';
    const baseColor = bodyStyles.color || '#000';
    return `<div style="font-family:${baseFont};font-size:${baseSize};font-weight:500;line-height:1.4;white-space:pre-wrap;color:${baseColor};">${container.innerHTML}</div>`;
  };

  const writeClipboardPayload = async (plain: string, html: string) => {
    const ClipboardItemConstructor = (window as any).ClipboardItem;
    if (ClipboardItemConstructor && navigator.clipboard?.write) {
      const item = new ClipboardItemConstructor({
        'text/plain': new Blob([plain], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' })
      });
      await navigator.clipboard.write([item]);
      return;
    }
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(plain);
    }
  };

  const buildCopyPayload = (text: string) => {
    const formatted = replaceMemberMentions(formatMentions(text));
    const container = document.createElement('div');
    container.innerHTML = formatted;

    stripCopyArtifacts(container);
    convertNewlinesToBr(container);

    container.querySelectorAll('pre').forEach((el) => {
      (el as HTMLElement).style.background = '#f3f4f6';
      (el as HTMLElement).style.borderRadius = '8px';
      (el as HTMLElement).style.padding = '0.6rem 0.75rem';
      (el as HTMLElement).style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace';
      (el as HTMLElement).style.fontSize = '0.85em';
      (el as HTMLElement).style.lineHeight = '1.4';
      (el as HTMLElement).style.whiteSpace = 'pre';
      (el as HTMLElement).style.margin = '0.35rem 0';
    });

    container.querySelectorAll('code').forEach((el) => {
      const isBlock = el.parentElement?.tagName.toLowerCase() === 'pre';
      if (isBlock) return;
      (el as HTMLElement).style.background = '#f3f4f6';
      (el as HTMLElement).style.borderRadius = '4px';
      (el as HTMLElement).style.padding = '0.05rem 0.3rem';
      (el as HTMLElement).style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace';
      (el as HTMLElement).style.fontSize = '0.85em';
      (el as HTMLElement).style.whiteSpace = 'pre-wrap';
    });

    const html = buildClipboardHtml(container);
    return { plain: htmlToPlainText(container), html };
  };

  const handleMessageTextCopy = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!event.currentTarget.contains(range.commonAncestorContainer)) return;

    const container = document.createElement('div');
    container.appendChild(range.cloneContents());
    stripCopyArtifacts(container);
    convertNewlinesToBr(container);

    const plain = htmlToPlainText(container);
    const html = buildClipboardHtml(container);

    if (event.clipboardData?.setData) {
      event.clipboardData.setData('text/plain', plain);
      event.clipboardData.setData('text/html', html);
      event.preventDefault();
      return;
    }

    event.preventDefault();
    void writeClipboardPayload(plain, html).catch((error) => {
      console.error('Failed to copy selection:', error);
    });
  };

  const handleCopyMessage = async (message: Message) => {
    if (!message.text) return;
    const { plain, html } = buildCopyPayload(message.text);
    try {
      await writeClipboardPayload(plain, html);
      setCopiedMessageId(message.id);
      window.setTimeout(() => {
        setCopiedMessageId(prev => (prev === message.id ? null : prev));
      }, 1500);
    } catch (error) {
      console.error('Failed to copy message:', error);
    }
  };

  const decodeHtmlEntities = (value: string) => {
    if (!value) return '';
    const decoder = document.createElement('textarea');
    decoder.innerHTML = value;
    return decoder.value;
  };

  const applySearchHighlight = (nodes: React.ReactNode[], keyPrefix: string, regex: RegExp | null) => {
    if (!regex) return nodes;
    const result: React.ReactNode[] = [];
    nodes.forEach((node, index) => {
      if (typeof node !== 'string') {
        result.push(node);
        return;
      }
      const safeRegex = new RegExp(regex.source, regex.flags);
      const matches = Array.from(node.matchAll(safeRegex));
      if (matches.length === 0) {
        result.push(node);
        return;
      }
      let lastIndex = 0;
      matches.forEach((match, matchIndex) => {
        const start = match.index ?? 0;
        if (start > lastIndex) {
          result.push(node.slice(lastIndex, start));
        }
        const value = match[0] ?? '';
        if (value) {
          result.push(
            <span key={`${keyPrefix}-search-${index}-${matchIndex}-${start}`} className="search-highlight">
              {value}
            </span>
          );
        }
        lastIndex = start + value.length;
      });
      if (lastIndex < node.length) {
        result.push(node.slice(lastIndex));
      }
    });
    return result;
  };

  const renderInlineFormattedText = (formatted: string, keyPrefix = 'fmt', searchRegex: RegExp | null = null) => {
    const tagPattern = /<\/?(b|strong|i|em|u|s|strike)>/gi;
    const tokens: Array<{ type: 'text'; value: string } | { type: 'tag'; name: string; closing: boolean }> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = tagPattern.exec(formatted)) !== null) {
      const matchIndex = match.index ?? 0;
      if (matchIndex > lastIndex) {
        tokens.push({ type: 'text', value: formatted.slice(lastIndex, matchIndex) });
      }
      const rawName = match[1].toLowerCase();
      const name = rawName === 'strong' ? 'b' : rawName === 'em' ? 'i' : rawName === 'strike' ? 's' : rawName;
      const closing = match[0].startsWith('</');
      tokens.push({ type: 'tag', name, closing });
      lastIndex = matchIndex + match[0].length;
    }
    if (lastIndex < formatted.length) {
      tokens.push({ type: 'text', value: formatted.slice(lastIndex) });
    }

    const stack: Array<{ name: string; children: React.ReactNode[] }> = [{ name: 'root', children: [] }];
    let tokenIndex = 0;

    tokens.forEach((token) => {
      if (token.type === 'text') {
        const nodes = normalizeNodes(renderHighlightedText(token.value, `${keyPrefix}-txt-${tokenIndex}`));
        const searchHighlighted = applySearchHighlight(nodes, `${keyPrefix}-txt-${tokenIndex}`, searchRegex);
        const linkified = linkifyNodes(searchHighlighted, `${keyPrefix}-txt-${tokenIndex}`);
        const withLineBreaks = applyLineBreaksToNodes(linkified, `${keyPrefix}-txt-${tokenIndex}`);
        stack[stack.length - 1].children.push(...withLineBreaks);
        tokenIndex += 1;
        return;
      }

      if (!token.closing) {
        stack.push({ name: token.name, children: [] });
        return;
      }

      const frame = stack.pop();
      if (!frame || frame.name !== token.name) {
        // Mismatched tag, treat as text
        const nodes = normalizeNodes(renderHighlightedText(`</${token.name}>`, `${keyPrefix}-txt-${tokenIndex}`));
        stack[stack.length - 1].children.push(...nodes);
        tokenIndex += 1;
        return;
      }

      const children = frame.children;
      let wrapped: React.ReactNode = children;
      if (frame.name === 'b') {
        wrapped = <strong key={`${keyPrefix}-b-${tokenIndex}`}>{children}</strong>;
      } else if (frame.name === 'i') {
        wrapped = <em key={`${keyPrefix}-i-${tokenIndex}`}>{children}</em>;
      } else if (frame.name === 'u') {
        wrapped = <u key={`${keyPrefix}-u-${tokenIndex}`}>{children}</u>;
      } else if (frame.name === 's') {
        wrapped = <s key={`${keyPrefix}-s-${tokenIndex}`}>{children}</s>;
      }
      stack[stack.length - 1].children.push(wrapped);
      tokenIndex += 1;
    });

    return stack[0].children;
  };

  const renderFormattedText = (text: string, options?: { highlightSearch?: boolean }) => {
    const formatted = replaceMemberMentions(formatMentions(text));
    const searchTermValue = options?.highlightSearch && isSearchActive ? searchTerm.trim() : '';
    const searchRegex = searchTermValue ? new RegExp(escapeRegExp(searchTermValue), 'gi') : null;
    const codeBlockPattern = /<pre><code([^>]*)>([\s\S]*?)<\/code><\/pre>|<pre([^>]*)>([\s\S]*?)<\/pre>|<code([^>]*)>([\s\S]*?)<\/code>/gi;
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let blockIndex = 0;

    while ((match = codeBlockPattern.exec(formatted)) !== null) {
      const matchIndex = match.index ?? 0;
      if (matchIndex > lastIndex) {
        const segment = formatted.slice(lastIndex, matchIndex);
        elements.push(...renderInlineFormattedText(segment, `seg-${blockIndex}`, searchRegex));
      }

      let rawAttrs = '';
      let codeHtml = '';
      let renderInline = false;

      if (match[1] !== undefined) {
        rawAttrs = match[1] || '';
        codeHtml = match[2] || '';
      } else if (match[3] !== undefined) {
        rawAttrs = match[3] || '';
        codeHtml = match[4] || '';
      } else {
        rawAttrs = match[5] || '';
        codeHtml = match[6] || '';
        renderInline = !/[\r\n]/.test(codeHtml);
      }

      const classMatch = rawAttrs.match(/class\s*=\s*["']([^"']+)["']/i);
      const codeClassName = classMatch?.[1];
      const codeText = decodeHtmlEntities(codeHtml);
      const codeContent = searchRegex ? applySearchHighlight([codeText], `code-${blockIndex}-${matchIndex}`, searchRegex) : codeText;

      if (renderInline) {
        elements.push(
          <code
            key={`code-inline-${blockIndex}-${matchIndex}`}
            className={codeClassName ? `message-inline-code ${codeClassName}` : 'message-inline-code'}
          >
            {codeContent}
          </code>
        );
      } else {
        elements.push(
          <pre key={`code-${blockIndex}-${matchIndex}`} className="message-code-block">
            <code className={codeClassName}>{codeContent}</code>
          </pre>
        );
      }

      lastIndex = matchIndex + match[0].length;
      blockIndex += 1;
    }

    if (lastIndex < formatted.length) {
      const tail = formatted.slice(lastIndex);
      elements.push(...renderInlineFormattedText(tail, `seg-${blockIndex}`, searchRegex));
    }

    return elements;
  };

  const resetComposer = () => {
    setNewMessage('');
    setNewMessageHtml('');
    setShowMentionSuggestions(false);
    setMentionQuery('');
    setMentionAnchorIndex(null);
    setAttachments([]);
    setReplyingTo(null);
    setEditingMessage(null);
    if (messageInputRef.current) {
      messageInputRef.current.innerHTML = '';
    }
  };

  const persistDraft = useCallback((chatId: string, text: string, html: string) => {
    const hasContent = text.trim().length > 0 || html.trim().length > 0;
    if (hasContent) {
      const next = { text, html };
      draftsByChatRef.current.set(chatId, next);
      onDraftChange?.(chatId, next);
    } else {
      draftsByChatRef.current.delete(chatId);
      onDraftChange?.(chatId, null);
    }
  }, [onDraftChange]);

  const clearDraft = useCallback((chatId: string) => {
    draftsByChatRef.current.delete(chatId);
    onDraftChange?.(chatId, null);
  }, [onDraftChange]);

  const persistDraftToStorage = useCallback((chatId: string, text: string, html: string) => {
    try {
      const raw = localStorage.getItem('chatDrafts');
      const parsed = raw ? JSON.parse(raw) as Record<string, { text: string; html: string }> : {};
      const hasContent = text.trim().length > 0 || html.trim().length > 0;
      if (hasContent) {
        parsed[chatId] = { text, html };
      } else {
        delete parsed[chatId];
      }
      if (Object.keys(parsed).length === 0) {
        localStorage.removeItem('chatDrafts');
      } else {
        localStorage.setItem('chatDrafts', JSON.stringify(parsed));
      }
    } catch (error) {
      console.warn('Failed to persist draft on unload', error);
    }
  }, []);

  useEffect(() => {
    newMessageRef.current = newMessage;
    newMessageHtmlRef.current = newMessageHtml;
  }, [newMessage, newMessageHtml]);

  useEffect(() => {
    if (isEditing) return;
    const previousChatId = activeDraftChatIdRef.current;
    if (previousChatId && previousChatId !== chat?.id) {
      persistDraft(previousChatId, newMessageRef.current, newMessageHtmlRef.current);
    }
    activeDraftChatIdRef.current = chat?.id ?? null;

    if (!chat?.id) {
      resetComposer();
      return;
    }
    const cachedDraft = draftsByChatRef.current.get(chat.id);
    const nextDraft = cachedDraft ?? draft;
    resetComposer();
    if (nextDraft) {
      setNewMessage(nextDraft.text);
      setNewMessageHtml(nextDraft.html);
      if (messageInputRef.current) {
        messageInputRef.current.innerHTML = nextDraft.html;
      }
    }
  }, [chat?.id, isEditing, persistDraft]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const activeChatId = activeDraftChatIdRef.current;
      if (!activeChatId || isEditing) return;
      const text = newMessageRef.current;
      const html = newMessageHtmlRef.current;
      persistDraft(activeChatId, text, html);
      persistDraftToStorage(activeChatId, text, html);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isEditing, persistDraft, persistDraftToStorage]);

  const focusEditorEnd = (editor: HTMLElement) => {
    editor.focus();
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false);
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const startEditingMessage = (message: Message) => {
    if (!messageInputRef.current) return;
    const editor = messageInputRef.current;
    const decodedText = decodeIncomingText(message.text || '', message.sender !== 'me');
    const formatted = replaceMemberMentions(formatMentions(decodedText));
    editor.innerHTML = formatted;
    setNewMessage(editor.innerText);
    setNewMessageHtml(editor.innerHTML);
    setEditingMessage(message);
    setReplyingTo(null);
    setAttachments([]);
    setShowMentionSuggestions(false);
    setMentionQuery('');
    setMentionAnchorIndex(null);
    focusEditorEnd(editor);
  };

  const cancelEditing = () => {
    setIsSavingEdit(false);
    resetComposer();
  };

  const handleSendMessage = async () => {
    if (!chat) return;
    const messageToSend = trimMessageHtmlForTelegram(replaceMentionsForSend(newMessageHtml));
    const plainText = newMessage.replace(/\s+/g, '').trim();
    if (editingMessage) {
      if (!plainText) return;
      const editId = editingMessage.telegramMessageId ?? Number(editingMessage.id);
      if (!Number.isFinite(editId)) {
        alert('Не удалось редактировать сообщение');
        return;
      }
      const originalText = (editingMessage.text || '').trim();
      if (messageToSend.trim() === originalText) {
        cancelEditing();
        return;
      }
      try {
        setIsSavingEdit(true);
        await onEditMessage(chat.id, editId, messageToSend);
        resetComposer();
        clearDraft(chat.id);
      } catch (error) {
        console.error('Failed to edit message', error);
        const status = (error as any)?.response?.status;
        const detail = (error as any)?.response?.data?.detail;
        if (status === 400 && typeof detail === 'string' && detail.trim()) {
          alert(detail);
        } else {
          alert('Не удалось изменить сообщение');
        }
      } finally {
        setIsSavingEdit(false);
      }
      return;
    }

    if (plainText || attachments.length > 0) {
      // Use telegramMessageId if available (for replies to backend msgs), fallback to id if numeric
      // The backend expects the ID of the message being replied to.
      let replyId: number | undefined = undefined;
      if (replyingTo) {
        if (replyingTo.telegramMessageId !== undefined) {
          replyId = replyingTo.telegramMessageId;
        } else {
          // Fallback for optimistic messages or if telegramMessageId is missing for some reason
          // Try to parse id
          const numId = Number(replyingTo.id);
          if (!isNaN(numId)) {
            replyId = numId;
          }
        }
      }

      onSendMessage(messageToSend, attachments, replyId);
      clearDraft(chat.id);

      // Force scroll to bottom when sending a message
      isNearBottomRef.current = true;
      setIsNearBottom(true);
      requestAnimationFrame(() => {
        scrollToBottom('auto');
      });

      resetComposer();
    }
  };

  const encodeClientId = (clientId: number) => {
    return window.btoa(String(clientId));
  };

  const openScheduleForStudent = (studentId: number) => {
    const student = eligibleStudents.find(s => s.id === studentId);
    if (student) {
      setSelectedStudentName(student.name);
    }
    const encodedId = encodeClientId(studentId);
    setScheduleUrl(`https://innoprog-office-hours.vercel.app/?id=${encodedId}&embedded=1&legacy_chat=1`);
    setShowStudentPicker(false);
  };

  const fetchReportsAndProgram = async (studentId: number) => {
    setReportsLoading(true);
    try {
      const [reportsResponse, programResponse] = await Promise.all([
        fetch(`https://api.innoprog.ru/client/${studentId}/reports`),
        fetch(`https://api.innoprog.ru/client/${studentId}/program`)
      ]);

      if (reportsResponse.status === 401 || programResponse.status === 401) {
        handleUnauthorized();
        return;
      }

      if (reportsResponse.ok) {
        const reportsData = await reportsResponse.json();
        // Sort reports by week_start_date descending (newest first)
        const sortedReports = (reportsData.reports || []).sort((a: Report, b: Report) => {
          return new Date(b.week_start_date).getTime() - new Date(a.week_start_date).getTime();
        });
        setReports(sortedReports);
      } else {
        setReports([]);
      }

      if (programResponse.ok) {
        const programData = await programResponse.json();
        // Sort program by order_index
        const sortedProgram = (programData.program || []).sort((a: ProgramItem, b: ProgramItem) => {
          return a.order_index - b.order_index;
        });
        setProgram(sortedProgram);
      } else {
        setProgram([]);
      }
    } catch (error) {
      console.error('Failed to fetch reports/program:', error);
      setReports([]);
      setProgram([]);
    } finally {
      setReportsLoading(false);
    }
  };

  const openReportsForStudent = (studentId: number, studentName: string) => {
    setReportsStudentId(studentId);
    setReportsStudentName(studentName);
    setShowReportsModal(true);
    setReportsTab('reports');
    fetchReportsAndProgram(studentId);
  };

  const handleReportsClick = () => {
    if (eligibleStudents.length === 1) {
      openReportsForStudent(eligibleStudents[0].id, eligibleStudents[0].name);
    } else if (eligibleStudents.length > 1) {
      setShowReportsStudentPicker(true);
    }
  };

  const formatReportDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleReportsModalMouseDown = (e: React.MouseEvent) => {
    if (isMobile) return;
    e.preventDefault();
    setIsDraggingReportsModal(true);
    reportsModalDragStartPos.current = {
      x: e.clientX - reportsModalPos.x,
      y: e.clientY - reportsModalPos.y
    };
  };

  const copyAllReportsData = async () => {
    let text = '';

    // Add student name header
    if (reportsStudentName) {
      text += `Ученик: ${reportsStudentName}\n`;
      text += '='.repeat(50) + '\n\n';
    }

    // Add reports section
    text += 'ОТЧЕТЫ\n';
    text += '-'.repeat(50) + '\n\n';

    if (reports.length === 0) {
      text += 'Нет отчетов\n\n';
    } else {
      reports.forEach((report) => {
        text += `Отчет за неделю ${formatReportDate(report.week_start_date)}\n`;
        text += `Преподаватель: ${report.teacher_name}\n\n`;
        text += `${report.report_text}\n\n`;
        text += '-'.repeat(30) + '\n\n';
      });
    }

    // Add program section
    text += '\nПРОГРАММА ОБУЧЕНИЯ\n';
    text += '-'.repeat(50) + '\n\n';

    if (program.length === 0) {
      text += 'Программа обучения не найдена\n';
    } else {
      // Group by section
      const sections = new Map<string, ProgramItem[]>();
      program.forEach((item) => {
        const existing = sections.get(item.section) || [];
        existing.push(item);
        sections.set(item.section, existing);
      });

      sections.forEach((items, section) => {
        text += `${section}:\n`;
        items.forEach((item) => {
          const status = item.is_completed ? '[✓]' : '[ ]';
          text += `  ${status} ${item.topic}\n`;
        });
        text += '\n';
      });
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleScheduleClick = () => {
    if (eligibleStudents.length === 1) {
      openScheduleForStudent(eligibleStudents[0].id);
    } else if (eligibleStudents.length > 1) {
      setShowStudentPicker(true);
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const editor = messageInputRef.current;
    if (editor) {
      editor.focus();
      insertTextAtCursor(emojiData.emoji);
      setNewMessage(editor.innerText);
      setNewMessageHtml(editor.innerHTML);
      updateMentionState(editor.innerText, undefined, editor);
    } else {
      setNewMessage(prev => prev + emojiData.emoji);
    }
    setShowMentionSuggestions(false);
    setMentionQuery('');
    setMentionAnchorIndex(null);
    // Optional: focus back on textarea
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const isMod = e.metaKey || e.ctrlKey;
    if (isMod && !e.shiftKey && e.key.toLowerCase() === 'b') {
      e.preventDefault();
      applyInlineFormat('bold');
      return;
    }
    if (isMod && !e.shiftKey && e.key.toLowerCase() === 'i') {
      e.preventDefault();
      applyInlineFormat('italic');
      return;
    }
    if (isMod && !e.shiftKey && e.key.toLowerCase() === 'u') {
      e.preventDefault();
      applyInlineFormat('underline');
      return;
    }
    if (isMod && e.shiftKey && e.key.toLowerCase() === 'x') {
      e.preventDefault();
      applyInlineFormat('strikeThrough');
      return;
    }

    if (e.key === 'ArrowUp' && !isEditing && !showMentionSuggestions && !isMod) {
      const editor = messageInputRef.current;
      const isEmpty = (editor?.innerText || '').trim().length === 0;
      if (isEmpty && attachments.length === 0) {
        const lastEditable = findLastEditableMessage();
        if (lastEditable) {
          e.preventDefault();
          startEditingMessage(lastEditable);
          return;
        }
      }
    }

    if (showMentionSuggestions && mentionSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveMentionIndex((prev) => (prev < 0 ? 0 : (prev + 1) % mentionSuggestions.length));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveMentionIndex((prev) => (prev < 0 ? mentionSuggestions.length - 1 : (prev - 1 + mentionSuggestions.length) % mentionSuggestions.length));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (activeMentionIndex >= 0) {
          e.preventDefault();
          handleSelectMention(mentionSuggestions[activeMentionIndex]);
          return;
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentionSuggestions(false);
        return;
      }
    }

    if (e.key === 'Escape' && isEditing) {
      e.preventDefault();
      cancelEditing();
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      const keyboardEvent = e.nativeEvent as KeyboardEvent & {
        sourceCapabilities?: {
          firesTouchEvents?: boolean;
        };
      };
      const isTouchKeyboardEvent = keyboardEvent.sourceCapabilities?.firesTouchEvents === true;
      const isIPadUsingExternalKeyboard =
        isIPad &&
        isTouchDevice &&
        isVirtualKeyboardVisible === false &&
        !isTouchKeyboardEvent;

      if (isIPad && !isIPadUsingExternalKeyboard) return;
      if ((isMobile || isTouchDevice) && !isIPad) return;
      e.preventDefault();
      void handleSendMessage();
    }
  };

  const handleFileSelect = (files: FileList | null) => {
    if (isEditing) return;
    if (files) {
      const fileArray = Array.from(files);
      setAttachments(prev => [...prev, ...fileArray]);
    }
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatTime = (timestamp: Date) => {
    return timestamp.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timestamp: Date) => {
    return timestamp.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long'
    });
  };

  const isGroupSender = (message: Message) => {
    const raw = message.senderRawName?.trim();
    const sender = message.sender?.trim();
    return raw === '@Group' || sender === '@Group';
  };

  const getEditableMessageId = (message: Message) => {
    if (message.telegramMessageId !== undefined) return message.telegramMessageId;
    const numericId = Number(message.id);
    return Number.isFinite(numericId) ? numericId : null;
  };

  const canEditMessage = (message: Message) => {
    if (message.sender !== 'me' || message.isSending) return false;
    if (isGroupSender(message)) return false;
    return getEditableMessageId(message) !== null;
  };

  const canDeleteMessage = (message: Message) => {
    if (message.sender !== 'me' || message.isSending) return false;
    if (isGroupSender(message)) return false;
    return getEditableMessageId(message) !== null;
  };

  const handleDeleteMessage = async (message: Message) => {
    if (!chat) return;
    const deleteId = getEditableMessageId(message);
    if (!deleteId) {
      alert('Не удалось удалить сообщение');
      return;
    }
    setPendingDeleteMessage(message);
  };

  const confirmDeleteMessage = async () => {
    if (!chat || !pendingDeleteMessage) return;
    const deleteId = getEditableMessageId(pendingDeleteMessage);
    if (!deleteId) {
      alert('Не удалось удалить сообщение');
      setPendingDeleteMessage(null);
      return;
    }
    try {
      setDeletingMessageId(pendingDeleteMessage.id);
      await onDeleteMessage(chat.id, deleteId);
      if (editingMessage?.id === pendingDeleteMessage.id) {
        cancelEditing();
      }
    } catch (error) {
      console.error('Failed to delete message', error);
      alert('Не удалось удалить сообщение');
    } finally {
      setDeletingMessageId(null);
      setPendingDeleteMessage(null);
    }
  };

  const findLastEditableMessage = () => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (canEditMessage(messages[i])) return messages[i];
    }
    return null;
  };

  const formatDateForHeader = (timestamp: Date) => {
    return timestamp.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long'
    });
  };

  const isSameDay = (date1: Date, date2: Date) => {
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  };

  const DateSeparator: React.FC<{ date: Date }> = ({ date }) => (
    <div className="date-separator">
      <span className="date-separator-text">{formatDate(date)}</span>
    </div>
  );

  const handleDownload = async (url: string, filename: string) => {
    try {
      const response = await apiClient.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to download file", error);
      alert("Failed to download file");
    }
  };

  // Обработчики touch событий для swipe слева направо
  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;

    // Проверяем, что мы на мобильной версии (ширина экрана < 768px)
    const isMobile = window.innerWidth < 768;
    const target = e.target as HTMLElement | null;

    if (target && target.closest('.message-input, .message-input-container, .message-input-editor')) {
      return;
    }

    // Начинаем отслеживать на мобильной версии
    if (onBack && isMobile) {
      setTouchState({
        startX,
        startY,
        startTime: Date.now(),
        isTracking: true,
        currentX: startX,
        hasMoved: false
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchState.isTracking) return;

    e.preventDefault(); // Предотвращаем прокрутку страницы

    const touch = e.touches[0];
    const currentX = touch.clientX;
    const deltaX = currentX - touchState.startX;
    const deltaY = Math.abs(touch.clientY - touchState.startY);

    // Определяем, есть ли движение (минимальное смещение 5px и горизонтальное > вертикального)
    const hasMoved = Math.abs(deltaX) > 5 && deltaX > deltaY;

    // Обновляем текущую позицию только если есть движение
    setTouchState(prev => ({
      ...prev,
      currentX: currentX,
      hasMoved: hasMoved || prev.hasMoved
    }));
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchState.isTracking) return;

    const touch = e.changedTouches[0];
    const endX = touch.clientX;
    const endY = touch.clientY;
    const deltaX = endX - touchState.startX;
    const deltaY = Math.abs(endY - touchState.startY);
    const deltaTime = Date.now() - touchState.startTime;

    // Проверяем условия для swipe назад:
    // 1. Горизонтальное движение больше вертикального (не диагональный swipe)
    // 2. Движение вправо (deltaX > 0)
    // 3. Минимальная длина swipe (30px - меньше, чем было)
    // 4. Максимальная продолжительность (800ms - дольше, чем было)
    // 5. Минимальная скорость (100px/s - меньше, чем было)
    const isHorizontalSwipe = deltaX > deltaY;
    const isSwipeRight = deltaX > 0;
    const minSwipeDistance = 30; // Уменьшили с 50px до 30px
    const maxSwipeTime = 800; // Увеличили с 500ms до 800ms
    const minSwipeVelocity = 100; // Уменьшили с 200px/s до 100px/s

    if (isHorizontalSwipe && isSwipeRight && deltaX >= minSwipeDistance &&
      deltaTime <= maxSwipeTime && (deltaX / deltaTime * 1000) >= minSwipeVelocity) {
      setIsSearching(false);
      setSearchTerm('');
      onBack?.();
    }

    // Сбрасываем состояние
    setTouchState({
      startX: 0,
      startY: 0,
      startTime: 0,
      isTracking: false,
      currentX: 0,
      hasMoved: false
    });
  };

  const handleBack = () => {
    setIsSearching(false);
    setSearchTerm('');
    onBack?.();
  };

  const getReactionMessageId = (message: Message): number | null => {
    if (typeof message.telegramMessageId === 'number') return message.telegramMessageId;
    const numericId = Number(message.id);
    return Number.isNaN(numericId) ? null : numericId;
  };

  const getMessageReactionKey = (message: Message): string => (
    message.telegramMessageId ? `tg:${message.telegramMessageId}` : `id:${message.id}`
  );

  const canReactToMessage = (message: Message): boolean => (
    Boolean(onToggleReaction) && Boolean(chat) && !message.isSending && getReactionMessageId(message) !== null
  );

  const getAvailableReactions = (message: Message): string[] => {
    const base = allowedReactions ?? [];
    const fromMessage = message.reactions?.map((reaction) => reaction.emoji) ?? [];
    const combined = [...base, ...fromMessage];
    return Array.from(new Set(combined));
  };

  const openReactionPickerForMessage = (message: Message) => {
    if (!canReactToMessage(message)) return;
    const hasMyReaction = message.reactions?.some((reaction) => reaction.reactedByMe) ?? false;
    if (hasMyReaction) return;
    const reactionKey = getMessageReactionKey(message);
    setReactionPickerMessageId(reactionKey);
  };

  const renderMessage = (message: Message, index: number) => {
    const getAvatarInitials = (sender: string) => {
      if (sender === 'me') return 'Я';
      return sender.substring(0, 2).toUpperCase();
    };

    const getAvatarColor = (sender: string) => {
      if (sender === 'me') return '#9C78FF';
      // Генерируем цвет на основе имени отправителя
      const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b', '#eb4d4b', '#6c5ce7', '#9C78FF', '#8c66e9'];
      let hash = 0;
      for (let i = 0; i < sender.length; i++) {
        hash = sender.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    };

    const senderDisplayName = message.sender === 'me'
      ? 'me'
      : resolveDisplayNameForSender(message.senderId, message.senderRawName, message.sender);
    const replyDisplayName = message.replyToMessage
      ? (message.replyToMessage.sender === 'me'
        ? 'me'
        : resolveDisplayNameForSender(message.replyToMessage.senderId, message.replyToMessage.senderRawName, message.replyToMessage.sender))
      : null;

    const shouldHighlightSearch = isSearchActive;
    const replyText = message.replyToMessage?.text
      ? renderFormattedText(decodeIncomingText(message.replyToMessage.text, message.replyToMessage.sender !== 'me'))
      : (message.replyToMessage?.messageType === 'photo' ? 'Photo' : 'File');
    const messageText = message.text
      ? renderFormattedText(decodeIncomingText(message.text, message.sender !== 'me'), { highlightSearch: shouldHighlightSearch })
      : '';

    const highlightKey = message.telegramMessageId
      ? `tg:${message.telegramMessageId}`
      : `id:${message.id}`;
    const isHighlighted = highlightKey === highlightedMessageId;
    const reactionKey = getMessageReactionKey(message);
    const reactions = message.reactions?.filter(reaction => reaction.count > 0) ?? [];
    const isReactionPickerOpen = reactionPickerMessageId === reactionKey;
    const reactionOptions = getAvailableReactions(message);
    const hasMyReaction = reactions.some((reaction) => reaction.reactedByMe);

    return (
      <div
        key={message.id}
        className={`message ${message.sender === 'me' ? 'own' : 'other'} ${isHighlighted ? 'highlighted' : ''}`}
        onDoubleClick={() => setReplyingTo(message)}
        onContextMenu={(event) => {
          if (!canReactToMessage(message)) return;
          event.preventDefault();
          openReactionPickerForMessage(message);
        }}
        onTouchStart={() => {
          if (!canReactToMessage(message)) return;
          if (reactionLongPressRef.current) {
            window.clearTimeout(reactionLongPressRef.current);
          }
          reactionLongPressRef.current = window.setTimeout(() => {
            openReactionPickerForMessage(message);
          }, 450);
        }}
        onTouchEnd={() => {
          if (reactionLongPressRef.current) {
            window.clearTimeout(reactionLongPressRef.current);
            reactionLongPressRef.current = null;
          }
        }}
        onTouchMove={() => {
          if (reactionLongPressRef.current) {
            window.clearTimeout(reactionLongPressRef.current);
            reactionLongPressRef.current = null;
          }
        }}
        data-message-index={index}
      >
        <div className="message-wrapper">
          <div className={`message-content ${message.isSending ? 'sending' : ''}`}>
            {message.sender !== 'me' && (
              <div className="sender-name" style={{ color: getAvatarColor(senderDisplayName) }}>
                {senderDisplayName}
              </div>
            )}
            {message.replyToMessage && (
              <div
                className="message-reply-context"
                onClick={(event) => {
                  event.stopPropagation();
                  const replyId = message.replyToMessageId ?? message.replyToMessage?.telegramMessageId;
                  if (!replyId) return;
                  pendingReplyScrollIdRef.current = replyId;
                  attemptScrollToReply();
                }}
              >
                <div className="reply-sender">{replyDisplayName === 'me' ? 'Вы' : replyDisplayName}</div>
                <div className="reply-text">{replyText}</div>
              </div>
            )}
            {message.attachments && message.attachments.length > 0 && (
              <div className="attachments">
                {message.attachments.map((attachment, index) => (
                  <div key={index} className="attachment">
                    {attachment.type === 'image' ? (
                      <div className="image-container">
                        <ProtectedImage
                          src={attachment.url}
                          alt={attachment.name}
                          className={`attachment-image ${message.isSending ? 'sending' : ''}`}
                          onClick={() => !message.isSending && setFullScreenImage(attachment.url)}
                          onLoad={handleImageLoad}
                        />
                        {message.isSending && (
                          <div className="image-sending-overlay">
                            <div className="sending-spinner"></div>
                          </div>
                        )}
                      </div>
                    ) : attachment.type === 'audio' ? (
                      <div className="audio-container">
                        <ProtectedAudio src={attachment.url} />
                      </div>
                    ) : (
                      <div
                        className="attachment-file-link"
                        onClick={() => handleDownload(attachment.url, attachment.name)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="attachment-file">
                          <Paperclip size={16} />
                          <span>{attachment.name}</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {message.text && (
              <div className="message-text-window" onCopy={handleMessageTextCopy}>
                {messageText}
              </div>
            )}
            {reactions.length > 0 && (
              <div className="message-reactions">
                {reactions.map((reaction) => (
                  <button
                    key={reaction.emoji}
                    type="button"
                    className={`message-reaction ${reaction.reactedByMe ? 'active' : ''}`}
                    onClick={() => chat && onToggleReaction?.(chat.id, message, reaction.emoji)}
                    disabled={!canReactToMessage(message)}
                  >
                    <span className="reaction-emoji">{reaction.emoji}</span>
                    <span className="reaction-count">{reaction.count}</span>
                  </button>
                ))}
              </div>
            )}
            {isReactionPickerOpen && canReactToMessage(message) && !hasMyReaction && reactionOptions.length > 0 && (
              <div className="message-reaction-picker" ref={reactionPickerRef}>
                {reactionOptions.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    className="message-reaction-option"
                    onClick={() => {
                      if (!chat) return;
                      onToggleReaction?.(chat.id, message, emoji);
                      setReactionPickerMessageId(null);
                    }}
                  >
                    <span className="reaction-emoji">{emoji}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="message-meta">
              <span className="message-time">
                {message.isSending ? 'Отправка...' : formatTime(message.timestamp)}
              </span>
              {message.editedAt && !message.isSending && (
                <span className="message-edited-label">изменено</span>
              )}
              {message.text && (
                <button
                  type="button"
                  className="message-copy-button"
                  onClick={() => handleCopyMessage(message)}
                >
                  {copiedMessageId === message.id ? <Check size={14} /> : <Copy size={14} />}
                </button>
              )}
              {canEditMessage(message) && (
                <button
                  type="button"
                  className="message-copy-button message-edit-button"
                  onClick={() => startEditingMessage(message)}
                  title="Редактировать сообщение"
                >
                  <Pencil size={14} />
                </button>
              )}
              {canDeleteMessage(message) && (
                <button
                  type="button"
                  className="message-copy-button message-delete-button"
                  onClick={() => void handleDeleteMessage(message)}
                  title="Удалить сообщение"
                  disabled={deletingMessageId === message.id}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
          <div
            className={`message-avatar${copiedAvatarMessageId === message.id ? ' copied' : ''}`}
            style={{ backgroundColor: getAvatarColor(senderDisplayName) }}
            onClick={() => handleCopyMemberId(message.id, message.senderId)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleCopyMemberId(message.id, message.senderId);
              }
            }}
            title={message.senderId ? `Скопировать ID: ${message.senderId}` : undefined}
          >
            {getAvatarInitials(senderDisplayName)}
          </div>
        </div>
      </div>
    );
  };

  if (!chat) {
    return (
      <div className="chat-window empty">
        <div className="empty-state">
          <div className="empty-icon">💬</div>
          <h3>Выберите чат</h3>
          <p>Выберите чат из списка слева, чтобы начать общение</p>
        </div>
      </div>
    );
  }

  // Вычисляем transform для визуальной обратной связи
  const getTransformStyle = () => {
    if (!touchState.isTracking || !touchState.hasMoved) return {};

    const deltaX = touchState.currentX - touchState.startX;
    if (deltaX <= 0) return {};

    // Ограничиваем максимальное смещение до 100px для плавной анимации
    const maxOffset = 100;
    const offset = Math.min(deltaX * 0.5, maxOffset); // Умножаем на 0.5 для плавности

    return {
      transform: `translateX(${offset}px)`,
      transition: 'none'
    };
  };

  return (
    <div
      ref={chatWindowRef}
      className={`chat-window ${touchState.isTracking && touchState.hasMoved ? 'swipe-active' : ''}`}
      style={getTransformStyle()}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className={`chat-window-header ${isSearching ? 'searching' : ''}`}>
        {onBack && (
          <button onClick={handleBack} className="back-button">
            <ArrowLeft size={20} />
          </button>
        )}

        {isSearching ? (
          <div className="message-search-container">
            <Search size={18} className="message-search-icon" />
            <input
              type="text"
              autoFocus
              placeholder="Search messages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="message-search-input"
            />
            <div className="message-search-controls">
              {searchTerm.length > 0 && (
                <span className={`search-match-count ${isSearchActive ? '' : 'inactive'}`}>
                  {isSearchActive ? (searchMatchCount > 0 ? `${searchPosition}/${searchMatchCount}` : '0') : 'мин. 3'}
                </span>
              )}
              <button
                type="button"
                className="search-nav-button"
                onClick={handlePrevSearchMatch}
                disabled={!isSearchActive || searchMatchCount === 0}
                aria-label="Предыдущее совпадение"
              >
                <ArrowUp size={16} />
              </button>
              <button
                type="button"
                className="search-nav-button"
                onClick={handleNextSearchMatch}
                disabled={!isSearchActive || searchMatchCount === 0}
                aria-label="Следующее совпадение"
              >
                <ArrowDown size={16} />
              </button>
              {searchTerm.length > 0 && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="clear-search-button"
                  aria-label="Очистить поиск"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="chat-header-info">
              <h2>{chat.name}</h2>
              {(() => {
                if (chat?.with_teacher) return null;
                const teacherLabel = chat?.teacherName
                  ?? (chat?.teacherNames && chat.teacherNames.length > 0 ? chat.teacherNames.join(', ') : null);
                if (!teacherLabel) return null;
                return (
                  <div className="teacher-info">
                    Преподаватель: {teacherLabel}
                  </div>
                );
              })()}
              {(() => {
                const studentWithLessons = eligibleStudents.find(s => s.lessons_total !== undefined && s.lessons_total !== null);
                if (studentWithLessons) {
                  return (
                    <div className="lesson-info">
                      {formatLessonsInfo(studentWithLessons.lessons_total || 0, studentWithLessons.lessons_scheduled || 0)}
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            {eligibleStudents.length > 0 && (
              <button
                onClick={handleReportsClick}
                className="reports-toggle-button"
                title="Отчеты"
              >
                <FileText size={20} />
              </button>
            )}
            <button onClick={() => setIsSearching(true)} className="search-toggle-button" title="Search messages">
              <Search size={20} />
            </button>
          </>
        )}
      </div>

      <div className="messages-container">
        {isInitializing && (
          <div className="messages-loading-overlay">
            <div className="messages-loading-spinner"></div>
            <span>Загрузка сообщений...</span>
          </div>
        )}
        {currentDateInView && !isInitializing && (
          <div className="sticky-date-header">
            <span className="sticky-date-text">{formatDateForHeader(currentDateInView)}</span>
          </div>
        )}
        <div
          className={`messages ${isInitializing ? 'messages-hidden' : ''}`}
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          onClickCapture={handleMessagesBlankClick}
        >
          {(() => {
            const filteredMessages = messages.filter(msg => !isGroupSender(msg));

            const elements: JSX.Element[] = [];
            let lastDate: Date | null = null;

            filteredMessages.forEach((message, filteredIndex) => {
              const messageDate = new Date(message.timestamp);

              // Добавляем разделитель даты если дата изменилась
              if (!lastDate || !isSameDay(lastDate, messageDate)) {
                elements.push(
                  <DateSeparator key={`date-${messageDate.getTime()}`} date={messageDate} />
                );
                lastDate = messageDate;
              }

              // Находим оригинальный индекс в массиве messages
              const originalIndex = messages.findIndex(msg => msg.id === message.id);
              if (firstUnreadIndex !== null && originalIndex === firstUnreadIndex) {
                elements.push(
                  <div key={`unread-${message.id}`} className="unread-divider" data-unread-divider="true">
                    <span className="unread-divider-text">Непрочитанное сообщение</span>
                  </div>
                );
              }
              elements.push(renderMessage(message, originalIndex));
            });

            return elements;
          })()}
        </div>
        {!isNearBottom && !isInitializing && (
          <button
            type="button"
            className="scroll-to-bottom"
            onClick={() => {
              // Mark all messages as read when jumping to bottom
              void markAllAsRead();
              suppressMarkReadUntilRef.current = Date.now() + 1200;
              scrollToBottom('smooth');
              isNearBottomRef.current = true;
              setIsNearBottom(true);
              isAtVeryBottomRef.current = true;
              setIsAtVeryBottom(true);
            }}
            title="В конец"
          >
            <ArrowDown size={18} />
          </button>
        )}
      </div>

      {attachments.length > 0 && (
        <div className="attachments-preview">
          {attachments.map((file, index) => (
            <div key={index} className="attachment-preview">
              {file.type.startsWith('image/') ? (
                <img src={URL.createObjectURL(file)} alt={file.name} />
              ) : (
                <div className="file-preview">
                  <Paperclip size={16} />
                  <span>{file.name}</span>
                </div>
              )}
              <button
                onClick={() => removeAttachment(index)}
                className="remove-attachment"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="message-input-container">
        {editingMessage && (
          <div className="reply-preview-bar edit-preview-bar">
            <div className="reply-preview-content edit-preview-content">
              <div className="reply-preview-header">
                <Pencil size={14} className="reply-icon edit-icon" />
                <span className="reply-preview-title">Редактирование сообщения</span>
              </div>
              <div className="reply-preview-text">
                {editingMessage.text
                  ? renderFormattedText(decodeIncomingText(editingMessage.text, editingMessage.sender !== 'me'))
                  : 'Сообщение'}
              </div>
            </div>
            <button onClick={cancelEditing} className="reply-close-button" title="Отменить редактирование">
              <X size={16} />
            </button>
          </div>
        )}
        {replyingTo && (
          <div className="reply-preview-bar">
            <div className="reply-preview-content">
              <div className="reply-preview-header">
                <Reply size={14} className="reply-icon" />
                <span className="reply-preview-title">
                  В ответ {replyingTo.sender === 'me'
                    ? 'себе'
                    : resolveDisplayNameForSender(replyingTo.senderId, replyingTo.senderRawName, replyingTo.sender)}
                </span>
              </div>
              <div className="reply-preview-text">
                {replyingTo.text
                  ? renderFormattedText(decodeIncomingText(replyingTo.text, replyingTo.sender !== 'me'))
                  : (replyingTo.attachments?.length ? 'Вложение' : 'Сообщение')}
              </div>
            </div>
            <button onClick={() => setReplyingTo(null)} className="reply-close-button">
              <X size={16} />
            </button>
          </div>
        )}
        <div className="message-input">
          <div className="input-controls">

            <button
              onClick={handleScheduleClick}
              className={`schedule-button ${isScheduleButtonCompact ? 'compact' : ''}`}
              disabled={eligibleStudents.length === 0}
              title={eligibleStudents.length === 0 ? 'Нет доступных преподавателей' : 'Записать на занятие'}
            >
              {isScheduleButtonCompact ? (
                <CalendarPlus size={18} />
              ) : (
                'Записать на занятие'
              )}
            </button>

            <button
              onClick={() => fileInputRef.current?.click()}
              className="attach-button"
              title="Прикрепить файл"
              disabled={isEditing}
            >
              <Paperclip size={20} />
            </button>
          </div>

          <div className="input-field-wrapper">
            {showMentionSuggestions && mentionSuggestions.length > 0 && (
              <div className="mention-suggestions" role="listbox">
                {mentionSuggestions.map((candidate, index) => (
                  <button
                    key={candidate.id}
                    type="button"
                    className={`mention-suggestion ${index === activeMentionIndex && activeMentionIndex >= 0 ? 'active' : ''}`}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      handleSelectMention(candidate);
                    }}
                  >
                    <span className="mention-name">{candidate.name}</span>
                  </button>
                ))}
              </div>
            )}
            {formatMenu.visible && (
              <div
                className="formatting-menu"
                style={{ left: formatMenu.x, top: formatMenu.y }}
              >
                <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={() => applyInlineFormat('bold')}>
                  <strong>B</strong>
                </button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={() => applyInlineFormat('italic')}>
                  <em>I</em>
                </button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={() => applyInlineFormat('underline')}>
                  <u>U</u>
                </button>
                <button type="button" onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }} onClick={() => applyInlineFormat('strikeThrough')}>
                  <s>S</s>
                </button>
              </div>
            )}
            <div
              ref={messageInputRef}
              className="message-input-editor"
              contentEditable
              suppressContentEditableWarning
              onInput={handleMessageChange}
              onKeyDown={handleKeyDown}
              onContextMenu={handleContextMenu}
              data-placeholder={isEditing ? 'Редактируйте сообщение...' : 'Напишите сообщение...'}
            />
            <div className="emoji-picker-wrapper" ref={emojiPickerRef}>
              <button
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className={`emoji-toggle-button ${showEmojiPicker ? 'active' : ''}`}
                title="Добавить эмодзи"
              >
                <Smile size={20} />
              </button>
              {showEmojiPicker && (
                <div className="emoji-picker-container">
                  <EmojiPicker
                    onEmojiClick={handleEmojiClick}
                    autoFocusSearch={false}
                    theme={Theme.LIGHT}
                    width={300}
                    height={400}
                  />
                </div>
              )}
            </div>
          </div>

          <button
            onClick={() => void handleSendMessage()}
            disabled={isSavingEdit || (!newMessage.trim() && (!isEditing && attachments.length === 0))}
            className="send-button"
          >
            <Send size={22} />
          </button>
        </div>

        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFileSelect(e.target.files)}
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFileSelect(e.target.files)}
        />
      </div>

      {fullScreenImage && (
        <div className="fullscreen-overlay" onClick={() => setFullScreenImage(null)}>
          <div className="fullscreen-content" onClick={e => e.stopPropagation()}>
            <ProtectedImage
              src={fullScreenImage}
              alt="Full screen"
              className="fullscreen-image"
              onLoad={handleImageLoad}
            />
            <button className="fullscreen-close" onClick={() => setFullScreenImage(null)}>
              <X size={32} />
            </button>
          </div>
        </div>
      )}

      {pendingDeleteMessage && (
        <div
          className="delete-modal-overlay"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            if (deletingMessageId !== pendingDeleteMessage.id) {
              setPendingDeleteMessage(null);
            }
          }}
        >
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-header">
              <div className="delete-modal-icon">
                <Trash2 size={18} />
              </div>
              <h3>Удалить сообщение?</h3>
            </div>
            <p className="delete-modal-text">
              Сообщение будет удалено у всех участников.
            </p>
            <div className="delete-modal-preview">
              {pendingDeleteMessage.text
                ? renderFormattedText(decodeIncomingText(pendingDeleteMessage.text, pendingDeleteMessage.sender !== 'me'))
                : 'Сообщение'}
            </div>
            <div className="delete-modal-actions">
              <button
                type="button"
                className="delete-modal-cancel"
                onClick={() => setPendingDeleteMessage(null)}
                disabled={deletingMessageId === pendingDeleteMessage.id}
              >
                Отмена
              </button>
              <button
                type="button"
                className="delete-modal-confirm"
                onClick={() => void confirmDeleteMessage()}
                disabled={deletingMessageId === pendingDeleteMessage.id}
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {showStudentPicker && eligibleStudents.length > 1 && (
        <div className="schedule-overlay" onClick={() => setShowStudentPicker(false)}>
          <div className="schedule-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Кого записать?</h3>
            <div className="schedule-students">
              {eligibleStudents.map((student) => (
                <button
                  key={student.id}
                  className="schedule-student-button"
                  onClick={() => openScheduleForStudent(student.id)}
                >
                  {student.name}
                </button>
              ))}
            </div>
            <button className="schedule-close" onClick={() => setShowStudentPicker(false)}>Отмена</button>
          </div>
        </div>
      )}

      {showReportsStudentPicker && eligibleStudents.length > 1 && (
        <div className="schedule-overlay" onClick={() => setShowReportsStudentPicker(false)}>
          <div className="schedule-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Выберите ученика для отчета</h3>
            <div className="schedule-students">
              {eligibleStudents.map((student) => (
                <button
                  key={student.id}
                  className="schedule-student-button"
                  onClick={() => {
                    openReportsForStudent(student.id, student.name);
                    setShowReportsStudentPicker(false);
                  }}
                >
                  {student.name}
                </button>
              ))}
            </div>
            <button className="schedule-close" onClick={() => setShowReportsStudentPicker(false)}>Отмена</button>
          </div>
        </div>
      )}

      {scheduleUrl && (
        <div
          className="schedule-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setScheduleUrl(null);
          }}
        >
          <div
            ref={scheduleModalRef}
            className={`schedule-modal schedule-modal-wide draggable${isDraggingModal ? ' dragging' : ''}${isSafari && isSafariSchedulePositioned ? ' safari-positioned' : ''}`}
            onClick={(e) => e.stopPropagation()}
            style={
              isSafari && isSafariSchedulePositioned
                ? { left: `${modalPos.x}px`, top: `${modalPos.y}px` }
                : { transform: `translate3d(${modalPos.x}px, ${modalPos.y}px, 0)` }
            }
          >
            <div className="schedule-modal-header" onMouseDown={handleModalMouseDown}>
              <span>Записать на занятие{selectedStudentName ? `: ${selectedStudentName}` : ''}</span>
              <button className="schedule-modal-close-btn" onClick={() => setScheduleUrl(null)}>
                <X size={20} />
              </button>
            </div>
            <iframe
              src={scheduleUrl}
              title="Записать на занятие"
              className="schedule-iframe"
            />
          </div>
        </div>
      )}

      {showReportsModal && (
        <div
          className="schedule-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowReportsModal(false);
          }}
        >
          <div
            ref={reportsModalRef}
            className={`reports-modal draggable${isDraggingReportsModal ? ' dragging' : ''}${isSafari && isSafariReportsPositioned ? ' safari-positioned' : ''}`}
            onClick={(e) => e.stopPropagation()}
            style={
              isSafari && isSafariReportsPositioned
                ? { left: `${reportsModalPos.x}px`, top: `${reportsModalPos.y}px` }
                : { transform: `translate3d(${reportsModalPos.x}px, ${reportsModalPos.y}px, 0)` }
            }
          >
            <div className="reports-modal-header" onMouseDown={handleReportsModalMouseDown}>
              <span>Отчеты{reportsStudentName ? `: ${reportsStudentName}` : ''}</span>
              <button className="schedule-modal-close-btn" onClick={() => setShowReportsModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="reports-tabs">
              <button
                className={`reports-tab ${reportsTab === 'reports' ? 'active' : ''}`}
                onClick={() => setReportsTab('reports')}
              >
                Отчеты
              </button>
              <button
                className={`reports-tab ${reportsTab === 'program' ? 'active' : ''}`}
                onClick={() => setReportsTab('program')}
              >
                Программа обучения
              </button>
            </div>
            <div className="reports-content">
              {reportsLoading ? (
                <div className="reports-loading">
                  <div className="messages-loading-spinner"></div>
                  <span>Загрузка...</span>
                </div>
              ) : reportsTab === 'reports' ? (
                <div className="reports-list">
                  {reports.length === 0 ? (
                    <div className="reports-empty">Нет отчетов</div>
                  ) : (
                    reports.map((report) => (
                      <div key={report.id} className="report-item">
                        <div className="report-header">
                          <span className="report-title">
                            Отчет за неделю {formatReportDate(report.week_start_date)}
                          </span>
                          <span className="report-teacher">{report.teacher_name}</span>
                        </div>
                        <div className="report-text">{report.report_text}</div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="program-table-container">
                  {program.length === 0 ? (
                    <div className="reports-empty">Программа обучения не найдена</div>
                  ) : (
                    <table className="program-table">
                      <thead>
                        <tr>
                          <th>Раздел</th>
                          <th>Тема</th>
                          <th>Выполнено</th>
                        </tr>
                      </thead>
                      <tbody>
                        {program.map((item) => (
                          <tr key={item.id} className={item.is_completed ? 'completed' : ''}>
                            <td>{item.section}</td>
                            <td>{item.topic}</td>
                            <td className="program-status">
                              {item.is_completed && <Check size={18} className="check-icon" />}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
            <div className="reports-footer">
              <button
                className={`copy-reports-button ${copySuccess ? 'success' : ''}`}
                onClick={copyAllReportsData}
                disabled={reportsLoading || copySuccess}
              >
                {copySuccess ? <Check size={18} /> : <Copy size={18} />}
                {copySuccess ? 'Текст скопирован' : 'Скопировать'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatWindow;
